import { VehicleTelemetry } from '../vehicle/VehiclePhysics';
import { StVOInfraction } from '../stvo/InfractionLog';
import { DashboardCluster } from './DashboardCluster';
import { TheoryQuizManager, TheoryQuestion } from '../stvo/TheoryQuizManager';

export interface PreDriveState {
  seatMirrorsAdjusted: boolean;
  seatbeltFastened: boolean;
  engineRunning: boolean;
  handbrakeReleased: boolean;
  gearSelected: boolean;
}

export class HUD {
  public container: HTMLElement;
  public dashboardCluster: DashboardCluster;
  public theoryQuizManager: TheoryQuizManager = new TheoryQuizManager();
  private btnCameraToggle!: HTMLButtonElement;
  private currentCameraMode: string = 'cockpit';

  private speedText!: HTMLElement;
  private rpmText!: HTMLElement;
  private gearText!: HTMLElement;
  private clockText!: HTMLElement;
  private speedLimitBadge!: HTMLElement;
  private trafficLightBadge!: HTMLElement;
  private blinkerL!: HTMLElement;
  private blinkerR!: HTMLElement;
  private stallAlert!: HTMLElement;
  private infractionBanner!: HTMLElement;

  // Live Telemetry HUD Widget
  private telemetryPill!: HTMLElement;
  private hudBlinkerL!: HTMLElement;
  private hudBlinkerR!: HTMLElement;
  private gearBadge!: HTMLElement;
  private speedNumEl!: HTMLElement;
  private accelBadge!: HTMLElement;
  private accelValEl!: HTMLElement;
  private accelBarEl!: HTMLElement;
  private gearButton!: HTMLElement;
  private speedBadgeBottom!: HTMLElement;
  public showClusterInCockpit: boolean = false;
  private lastSpeedMs: number = 0;
  private smoothedAccelMs2: number = 0;

  // In-Drive StVO Theory Quiz (Fahrsimulator Edition)
  private theoryQuizCard!: HTMLElement;
  private currentQuizQuestion: TheoryQuestion | null = null;
  private quizSelectedOption: number = -1;
  private quizFeedbackEl!: HTMLElement;
  private quizOptionsContainer!: HTMLElement;
  private quizContextBadge!: HTMLElement;
  private quizTitleEl!: HTMLElement;
  private btnQuizSubmit!: HTMLButtonElement;
  
  // Dashboard indicator lamps
  private seatbeltLamp!: HTMLElement;
  private handbrakeLamp!: HTMLElement;
  private engineLamp!: HTMLElement;
  public btnMouseSteer!: HTMLElement;
  public btnWheelTelemetry!: HTMLElement;
  public btnTalkBackToggle!: HTMLElement;
  public talkBackEnabled: boolean = false;
  public wheelTelemetryCard!: HTMLElement;
  private wheelStatusBadge!: HTMLElement;
  private wheelSvgRotor!: HTMLElement;
  private wheelAngleLabel!: HTMLElement;
  private wheelThrottleBar!: HTMLElement;
  private wheelThrottlePct!: HTMLElement;
  private wheelBrakeBar!: HTMLElement;
  private wheelBrakePct!: HTMLElement;
  private wheelPaddleHint!: HTMLElement;
  private wheelAutoOpened: boolean = false;

  // Callbacks
  private onOpenSettingsCallback: (() => void) | null = null;
  private onOpenCockpitAdjusterCallback: (() => void) | null = null;
  private onToggleTrajectoryCallback: (() => void) | null = null;
  private onReturnToMenuCallback: (() => void) | null = null;
  private onToggleSeatbeltCallback: (() => void) | null = null;
  private onFastenSeatbeltCallback: (() => void) | null = null;
  private onToggleEngineCallback: (() => void) | null = null;
  private onStartEngineCallback: (() => void) | null = null;
  private onToggleHandbrakeCallback: (() => void) | null = null;
  private onReleaseHandbrakeCallback: (() => void) | null = null; // release-only (checklist "Lösen")
  private onSelectDriveGearCallback: (() => void) | null = null;
  private onToggleCameraCallback: (() => void) | null = null;
  private onBlinkerLeftCallback: (() => void) | null = null;
  private onBlinkerRightCallback: (() => void) | null = null;
  private onToggleMouseSteeringCallback: (() => void) | null = null;
  private onToggleTalkBackCallback: ((enabled: boolean) => void) | null = null;

  public onToggleMouseSteering(cb: () => void) {
    this.onToggleMouseSteeringCallback = cb;
  }

  public onToggleTalkBack(cb: (enabled: boolean) => void) {
    this.onToggleTalkBackCallback = cb;
  }

  public setTalkBackEnabled(enabled: boolean) {
    this.talkBackEnabled = enabled;
    if (this.btnTalkBackToggle) {
      if (enabled) {
        this.btnTalkBackToggle.innerHTML = '🔊 Talk-Back: AN [U]';
        this.btnTalkBackToggle.style.color = '#38bdf8';
        this.btnTalkBackToggle.style.background = 'rgba(2, 132, 199, 0.28)';
        this.btnTalkBackToggle.style.borderColor = '#38bdf8';
        this.btnTalkBackToggle.setAttribute('data-talkback', 'on');
      } else {
        this.btnTalkBackToggle.innerHTML = '🔇 Talk-Back: AUS [U]';
        this.btnTalkBackToggle.style.color = '#94a3b8';
        this.btnTalkBackToggle.style.background = 'rgba(30, 41, 59, 0.65)';
        this.btnTalkBackToggle.style.borderColor = '#475569';
        this.btnTalkBackToggle.setAttribute('data-talkback', 'off');
      }
    }
  }

  // Shoulder-check vignette overlay
  private shoulderVignette!: HTMLElement;
  private shoulderLabel!: HTMLElement;

  // ── Live Infraction Score Panel (right side) ──────────────────────────────
  private scorePanel!: HTMLElement;
  private scoreValue!: HTMLElement;
  private infractionList!: HTMLElement;
  private infractionRowMap = new Map<string, { row: HTMLElement; countEl: HTMLElement; count: number }>();

  // ── Active-violation warning strip ───────────────────────────────────────
  private activeViolationStrip!: HTMLElement;

  // ── Persistent Fahrprüfer Navigation Banner ──────────────────────────────
  private navigationBanner!: HTMLElement;
  private navIconEl!: HTMLElement;
  private navTitleEl!: HTMLElement;
  private navTextEl!: HTMLElement;
  private navSubTextEl!: HTMLElement;
  private btnNavReplay!: HTMLButtonElement;
  private onNavReplayCallback: (() => void) | null = null;

  // ── Pre-Drive Checklist Widget (Abfahrkontrolle) ──────────────────────────
  private checklistCard!: HTMLElement;
  private checklistBackdrop!: HTMLElement;
  private btnChecklistToggle!: HTMLButtonElement;
  private checklistAutoCloseTimer: any = null;
  private checklistItems: Map<string, { row: HTMLElement; statusEl: HTMLElement; btnAction?: HTMLButtonElement }> = new Map();
  public preDriveCompleted: boolean = false;
  public seatMirrorsAdjusted: boolean = false;

  constructor() {
    this.dashboardCluster = new DashboardCluster();
    this.container = document.createElement('div');
    this.container.id = 'driving-hud';
    this.container.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 36;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      padding: 16px;
      box-sizing: border-box;
    `;

    this.buildHUD();
  }

  private buildHUD() {
    // 1. Top Section: Speed Limit Badge, Pre-Drive, Camera & Settings Buttons
    const topRow = document.createElement('div');
    topRow.id = 'hud-top-row';
    topRow.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      width: 100%;
      pointer-events: none;
    `;

    // Inject HUD mobile-clean & landscape view styles
    if (!document.getElementById('hud-mobile-clean-css')) {
      const hudCleanStyle = document.createElement('style');
      hudCleanStyle.id = 'hud-mobile-clean-css';
      hudCleanStyle.textContent = `
        @media (max-width: 950px), (pointer: coarse) {
          #hud-top-row #hud-btn-camera-toggle,
          #hud-top-row #hud-btn-cockpit-adjust,
          #hud-top-row #hud-btn-settings,
          #hud-top-row #hud-btn-orientation-toggle {
            display: none !important;
          }
          #hud-quick-controls {
            display: none !important;
          }
          #hud-top-row {
            flex-wrap: wrap !important;
          }
          #hud-top-alert-wrapper {
            order: 3 !important;
            width: 100% !important;
            max-width: 100% !important;
            margin: 4px 0 0 0 !important;
          }
          #hud-accel-badge {
            display: none !important;
          }
        }
        .cockpit-clean-hud #hud-navigation-banner {
          display: none !important;
        }
        .cockpit-clean-hud #hud-telemetry-pill {
          display: none !important;
        }
        .cockpit-clean-hud #hud-quick-controls {
          display: none !important;
        }
        .cockpit-clean-hud #hud-score-panel {
          display: none !important;
        }
        @media (orientation: portrait) {
          #hud-top-row {
            padding: 0 4px !important;
            margin-top: 36px !important;
          }
          #hud-speed-limit-badge {
            width: 38px !important;
            height: 38px !important;
            font-size: 16px !important;
            border-width: 4px !important;
          }
          #hud-navigation-banner {
            top: 86px !important;
          }
          #hud-dashboard-canvas {
            max-height: 58px !important;
            max-width: 220px !important;
            opacity: 0.90 !important;
            margin-bottom: 2px !important;
          }
          #hud-bottom-container {
            margin-bottom: 95px !important;
            pointer-events: none !important;
          }
        }
        @media (orientation: landscape) and (max-height: 550px), (max-width: 950px) and (orientation: landscape) {
          #hud-top-row {
            padding: 0 4px !important;
          }
          #hud-top-left-group {
            margin-left: 116px !important;
            gap: 8px !important;
            align-items: center !important;
          }
          #hud-speed-limit-badge {
            width: 40px !important;
            height: 40px !important;
            font-size: 18px !important;
            border-width: 4.5px !important;
            box-shadow: 0 0 10px rgba(220, 38, 38, 0.4) !important;
          }
          #hud-telemetry-pill {
            padding: 0 10px !important;
            height: 38px !important;
            gap: 6px !important;
            border-color: rgba(56, 189, 248, 0.6) !important;
            box-shadow: 0 0 14px rgba(56, 189, 248, 0.25) !important;
          }
          #hud-speed-num {
            font-size: 28px !important;
            font-weight: 900 !important;
            color: #38bdf8 !important;
            text-shadow: 0 0 10px rgba(56, 189, 248, 0.7) !important;
            min-width: 36px !important;
            text-align: right !important;
          }
          #hud-accel-badge {
            display: none !important;
          }
          #hud-navigation-banner {
            top: 46px !important;
            padding: 6px 12px !important;
          }
          #hud-dashboard-canvas {
            max-height: 70px !important;
            max-width: 220px !important;
            opacity: 0.82 !important;
          }
          #hud-bottom-container {
            gap: 2px !important;
            margin-bottom: 0px !important;
          }
          #hud-active-violation-strip,
          #hud-infraction-banner {
            font-size: 11px !important;
            padding: 4px 10px !important;
            max-width: 380px !important;
          }
        }
      `;
      document.head.appendChild(hudCleanStyle);
    }

    const leftGroup = document.createElement('div');
    leftGroup.id = 'hud-top-left-group';
    leftGroup.style.cssText = `display: flex; align-items: center; gap: 10px; flex-wrap: wrap; pointer-events: auto; z-index: 38;`;

    // Camera Mode Switcher Button (Cockpit / Chase / Top-Down)
    this.btnCameraToggle = document.createElement('button');
    this.btnCameraToggle.id = 'hud-btn-camera-toggle';
    this.btnCameraToggle.innerHTML = '🎥 Cockpit [V]';
    this.btnCameraToggle.title = 'Kamera wechseln: Fahrer-Cockpit (1. Person) / Verfolger (3. Person) / Draufsicht (Taste C / V)';
    this.btnCameraToggle.style.cssText = `
      background: rgba(15, 23, 42, 0.9);
      border: 1.5px solid #38bdf8;
      color: #38bdf8;
      padding: 6px 14px;
      border-radius: 8px;
      font-size: 12px;
      font-weight: 700;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 6px;
      transition: all 0.2s ease;
      pointer-events: auto;
      touch-action: manipulation;
    `;
    this.btnCameraToggle.addEventListener('click', (e) => {
      (e.currentTarget as HTMLElement)?.blur();
      if (this.onToggleCameraCallback) this.onToggleCameraCallback();
    });
    leftGroup.appendChild(this.btnCameraToggle);

    // Speed limit sign badge
    this.speedLimitBadge = document.createElement('div');
    this.speedLimitBadge.id = 'hud-speed-limit-badge';
    this.speedLimitBadge.style.cssText = `
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: white;
      border: 6px solid #dc2626;
      box-shadow: 0 4px 12px rgba(0,0,0,0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      font-family: 'Chakra Petch', sans-serif;
      font-size: 20px;
      font-weight: 800;
      color: #0f172a;
    `;
    this.speedLimitBadge.innerText = '50';
    leftGroup.appendChild(this.speedLimitBadge);

    // Mini Live Traffic Light Status Indicator (visible near signalized junctions)
    this.trafficLightBadge = document.createElement('div');
    this.trafficLightBadge.id = 'hud-traffic-light-badge';
    this.trafficLightBadge.title = 'Ampel-Signalgeber Status';
    this.trafficLightBadge.style.cssText = `
      display: none;
      align-items: center;
      gap: 5px;
      padding: 6px 12px;
      background: rgba(15, 23, 42, 0.95);
      border: 1.5px solid rgba(255, 255, 255, 0.2);
      border-radius: 20px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.5);
    `;
    this.trafficLightBadge.innerHTML = `
      <div id="tl-indicator-r" style="width: 14px; height: 14px; border-radius: 50%; background: #450a0a; border: 1px solid #7f1d1d; transition: all 0.2s;"></div>
      <div id="tl-indicator-y" style="width: 14px; height: 14px; border-radius: 50%; background: #422006; border: 1px solid #78350f; transition: all 0.2s;"></div>
      <div id="tl-indicator-g" style="width: 14px; height: 14px; border-radius: 50%; background: #052e16; border: 1px solid #064e3b; transition: all 0.2s;"></div>
    `;
    leftGroup.appendChild(this.trafficLightBadge);

    // ─── Live Digital Telemetry Pill: Gear, Speed & Acceleration ───
    this.telemetryPill = document.createElement('div');
    this.telemetryPill.id = 'hud-telemetry-pill';
    this.telemetryPill.title = 'Live-Telemetrie: Gang, Geschwindigkeit & Beschleunigung';
    this.telemetryPill.style.cssText = `
      display: flex;
      align-items: center;
      gap: 10px;
      background: rgba(15, 23, 42, 0.92);
      border: 1.5px solid rgba(56, 189, 248, 0.45);
      border-radius: 21px;
      padding: 0 14px;
      height: 42px;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.65);
      font-family: 'Chakra Petch', monospace;
      color: #f8fafc;
      pointer-events: auto;
    `;

    // 0. Left Indicator Light Arrow
    this.hudBlinkerL = document.createElement('span');
    this.hudBlinkerL.id = 'hud-blinker-arrow-left';
    this.hudBlinkerL.innerHTML = '◀';
    this.hudBlinkerL.title = 'Blinker Links (Taste Q)';
    this.hudBlinkerL.style.cssText = `
      font-size: 20px;
      font-weight: 900;
      color: #64748b;
      opacity: 0.35;
      padding: 0 4px;
      cursor: pointer;
      user-select: none;
      transition: all 0.1s ease;
    `;
    this.hudBlinkerL.addEventListener('click', () => {
      if (this.onBlinkerLeftCallback) this.onBlinkerLeftCallback();
    });

    // 1. Current Gear Badge
    this.gearBadge = document.createElement('div');
    this.gearBadge.id = 'hud-gear-badge';
    this.gearBadge.title = 'Aktueller Fahrgang (P / R / N / D)';
    this.gearBadge.style.cssText = `
      background: rgba(30, 41, 59, 0.95);
      border: 2px solid #f59e0b;
      color: #fbbf24;
      padding: 3px 12px;
      border-radius: 8px;
      font-size: 18px;
      font-weight: 900;
      min-width: 40px;
      text-align: center;
      box-shadow: 0 0 12px rgba(245, 158, 11, 0.35);
      user-select: none;
    `;
    this.gearBadge.innerText = 'P';

    // 2. Digital Speedometer
    const speedWrap = document.createElement('div');
    speedWrap.id = 'hud-digital-speed';
    speedWrap.style.cssText = `display: flex; align-items: baseline; gap: 4px; padding: 0 4px;`;

    this.speedNumEl = document.createElement('span');
    this.speedNumEl.id = 'hud-speed-num';
    this.speedNumEl.style.cssText = `font-size: 28px; font-weight: 900; color: #38bdf8; min-width: 36px; text-align: right; letter-spacing: -0.02em; text-shadow: 0 0 10px rgba(56, 189, 248, 0.6);`;
    this.speedNumEl.innerText = '0';

    const speedUnit = document.createElement('span');
    speedUnit.style.cssText = `font-size: 12px; font-weight: 700; color: #94a3b8;`;
    speedUnit.innerText = 'km/h';

    speedWrap.appendChild(this.speedNumEl);
    speedWrap.appendChild(speedUnit);

    // 3. Right Indicator Light Arrow
    this.hudBlinkerR = document.createElement('span');
    this.hudBlinkerR.id = 'hud-blinker-arrow-right';
    this.hudBlinkerR.innerHTML = '▶';
    this.hudBlinkerR.title = 'Blinker Rechts (Taste E)';
    this.hudBlinkerR.style.cssText = `
      font-size: 20px;
      font-weight: 900;
      color: #64748b;
      opacity: 0.35;
      padding: 0 4px;
      cursor: pointer;
      user-select: none;
      transition: all 0.1s ease;
    `;
    this.hudBlinkerR.addEventListener('click', () => {
      if (this.onBlinkerRightCallback) this.onBlinkerRightCallback();
    });

    // 4. Live Acceleration & Force Display
    this.accelBadge = document.createElement('div');
    this.accelBadge.id = 'hud-accel-badge';
    this.accelBadge.title = 'Live-Beschleunigung / Verzögerung';
    this.accelBadge.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2px;
      padding: 2px 7px;
      background: rgba(30, 41, 59, 0.7);
      border: 1px solid rgba(148, 163, 184, 0.3);
      border-radius: 6px;
    `;
    this.accelValEl = document.createElement('span');
    this.accelValEl.id = 'hud-accel-val';
    this.accelValEl.style.cssText = `font-size: 10px; font-weight: 700; color: #94a3b8; font-family: monospace;`;
    this.accelValEl.innerText = '+0.0 m/s²';

    const accelBarWrap = document.createElement('div');
    accelBarWrap.style.cssText = `width: 38px; height: 3px; background: #334155; border-radius: 2px; overflow: hidden; position: relative;`;
    this.accelBarEl = document.createElement('div');
    this.accelBarEl.id = 'hud-accel-bar';
    this.accelBarEl.style.cssText = `width: 0%; height: 100%; background: #38bdf8; transition: width 0.08s;`;
    accelBarWrap.appendChild(this.accelBarEl);

    this.accelBadge.appendChild(this.accelValEl);
    this.accelBadge.appendChild(accelBarWrap);

    // 5. Live Score Badge inside Telemetry Pill (compact on mobile)
    const scorePillBadge = document.createElement('div');
    scorePillBadge.id = 'hud-score-pill-badge';
    scorePillBadge.title = 'Prüfungspunkte (100 Startpunkte)';
    scorePillBadge.style.cssText = `
      display: flex;
      align-items: center;
      gap: 3px;
      padding: 2px 7px;
      background: rgba(22, 101, 52, 0.55);
      border: 1px solid rgba(74, 222, 128, 0.4);
      border-radius: 6px;
      font-size: 11px;
      font-weight: 800;
      color: #4ade80;
    `;
    scorePillBadge.innerHTML = `<span>💯</span><span id="hud-score-pill-val">100</span>`;

    this.telemetryPill.appendChild(this.hudBlinkerL);
    this.telemetryPill.appendChild(this.gearBadge);
    this.telemetryPill.appendChild(speedWrap);
    this.telemetryPill.appendChild(this.hudBlinkerR);
    this.telemetryPill.appendChild(this.accelBadge);
    this.telemetryPill.appendChild(scorePillBadge);
    leftGroup.appendChild(this.telemetryPill);

    // Center: Top Alert Wrapper for Driving Mistakes & Infractions
    const topAlertWrapper = document.createElement('div');
    topAlertWrapper.id = 'hud-top-alert-wrapper';
    topAlertWrapper.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 6px;
      flex: 1;
      max-width: 620px;
      margin: 0 12px;
      pointer-events: none;
      z-index: 100;
    `;

    // Active Violation Warning Strip (shows real-time driving mistakes, e.g. crossing solid lines)
    this.activeViolationStrip = document.createElement('div');
    this.activeViolationStrip.id = 'hud-active-violation-strip';
    this.activeViolationStrip.style.cssText = `
      align-self: center;
      background: rgba(220, 38, 38, 0.95);
      color: white;
      font-family: 'Chakra Petch', sans-serif;
      font-size: 13px;
      font-weight: 700;
      padding: 7px 18px;
      border-radius: 10px;
      border: 1.5px solid #f87171;
      box-shadow: 0 4px 20px rgba(220, 38, 38, 0.55), 0 2px 8px rgba(0, 0, 0, 0.8);
      display: none;
      pointer-events: auto;
      max-width: 600px;
      text-align: center;
      letter-spacing: 0.02em;
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      transition: all 0.2s ease;
    `;
    topAlertWrapper.appendChild(this.activeViolationStrip);

    // Live Infraction Alert Banner (speeding, red light, stop sign, etc.)
    this.infractionBanner = document.createElement('div');
    this.infractionBanner.id = 'hud-infraction-banner';
    this.infractionBanner.style.cssText = `
      align-self: center;
      background: rgba(185, 28, 28, 0.95);
      color: white;
      font-family: 'Chakra Petch', sans-serif;
      padding: 7px 18px;
      border-radius: 10px;
      font-weight: 700;
      font-size: 13px;
      display: none;
      box-shadow: 0 4px 20px rgba(185, 28, 28, 0.5), 0 2px 8px rgba(0, 0, 0, 0.8);
      border: 1.5px solid #fca5a5;
      text-align: center;
      max-width: 600px;
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      transition: all 0.2s ease;
    `;
    topAlertWrapper.appendChild(this.infractionBanner);

    // Fahrprüfer Turn-by-Turn Navigation Banner
    this.navigationBanner = document.createElement('div');
    this.navigationBanner.id = 'hud-navigation-banner';
    this.navigationBanner.style.cssText = `
      position: absolute;
      top: 56px;
      left: 50%;
      transform: translateX(-50%);
      background: linear-gradient(135deg, rgba(15, 23, 42, 0.96) 0%, rgba(30, 58, 138, 0.94) 100%);
      color: #f8fafc;
      font-family: 'Chakra Petch', sans-serif;
      padding: 8px 16px;
      border-radius: 12px;
      border: 1.5px solid #38bdf8;
      box-shadow: 0 4px 20px rgba(2, 132, 199, 0.35), 0 2px 8px rgba(0, 0, 0, 0.8);
      display: none;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      pointer-events: auto;
      width: min(520px, 92vw);
      box-sizing: border-box;
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      z-index: 95;
      transition: all 0.2s ease;
    `;

    const navLeftGroup = document.createElement('div');
    navLeftGroup.style.cssText = `display:flex;align-items:center;gap:10px;flex:1;min-width:0;`;

    this.navIconEl = document.createElement('div');
    this.navIconEl.id = 'hud-nav-icon';
    this.navIconEl.style.cssText = `font-size:24px;min-width:32px;display:flex;align-items:center;justify-content:center;flex-shrink:0;`;
    this.navIconEl.innerHTML = '🧭';

    const navTextGroup = document.createElement('div');
    navTextGroup.style.cssText = `display:flex;flex-direction:column;gap:1px;min-width:0;flex:1;`;

    this.navTitleEl = document.createElement('div');
    this.navTitleEl.id = 'hud-nav-title';
    this.navTitleEl.style.cssText = `font-size:10px;font-weight:800;color:#38bdf8;text-transform:uppercase;letter-spacing:0.5px;`;
    this.navTitleEl.innerText = '🧭 FAHRPRÜFER MÜLLER';

    this.navTextEl = document.createElement('div');
    this.navTextEl.id = 'hud-nav-text';
    this.navTextEl.style.cssText = `font-size:13px;font-weight:700;color:#ffffff;line-height:1.25;white-space:normal;`;
    this.navTextEl.innerText = 'Fahrtrichtung wird ermittelt...';

    this.navSubTextEl = document.createElement('div');
    this.navSubTextEl.id = 'hud-nav-subtext';
    this.navSubTextEl.style.cssText = `font-size:11px;font-weight:600;color:#94a3b8;`;
    this.navSubTextEl.innerText = 'Dem Straßenverlauf folgen';

    navTextGroup.appendChild(this.navTitleEl);
    navTextGroup.appendChild(this.navTextEl);
    navTextGroup.appendChild(this.navSubTextEl);

    navLeftGroup.appendChild(this.navIconEl);
    navLeftGroup.appendChild(navTextGroup);

    this.btnNavReplay = document.createElement('button');
    this.btnNavReplay.id = 'hud-btn-nav-replay';
    this.btnNavReplay.innerHTML = '🔊';
    this.btnNavReplay.title = 'Anweisung vorlesen (Wiederholen)';
    this.btnNavReplay.style.cssText = `
      background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
      color: white;
      border: 1px solid #38bdf8;
      width: 38px;
      height: 38px;
      min-width: 38px;
      min-height: 38px;
      border-radius: 9px;
      font-size: 16px;
      font-weight: bold;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      touch-action: manipulation;
      box-shadow: 0 2px 8px rgba(2, 132, 199, 0.4);
      flex-shrink: 0;
    `;
    this.btnNavReplay.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.onNavReplayCallback) this.onNavReplayCallback();
    });

    this.navigationBanner.appendChild(navLeftGroup);
    this.navigationBanner.appendChild(this.btnNavReplay);

    this.container.appendChild(this.navigationBanner);

    // Right: Cockpit & Utility Controls
    const rightGroup = document.createElement('div');
    rightGroup.id = 'hud-top-right-group';
    rightGroup.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 8px;
      flex-wrap: wrap;
      flex-shrink: 0;
      pointer-events: auto;
    `;

    // In-Cockpit Adjuster Button (🪑 Sitz / Spiegel)
    const btnCockpit = document.createElement('button');
    btnCockpit.id = 'hud-btn-cockpit-adjust';
    btnCockpit.innerHTML = `🪑 <span style="font-size:12px;font-weight:700;margin-left:4px;">Sitz & Spiegel [J]</span>`;
    btnCockpit.title = 'Sitzhöhe, Sitztiefe und Spiegel im Cockpit justieren [J]';
    btnCockpit.style.cssText = `
      background: rgba(15, 23, 42, 0.9);
      border: 1.5px solid #3b82f6;
      color: #60a5fa;
      padding: 0 14px;
      height: 42px;
      border-radius: 21px;
      font-family: 'Chakra Petch', sans-serif;
      font-size: 14px;
      cursor: pointer;
      display: flex;
      align-items: center;
      box-shadow: 0 4px 14px rgba(59, 130, 246, 0.3);
      transition: background 0.15s, transform 0.1s;
      pointer-events: auto;
      touch-action: manipulation;
    `;
    btnCockpit.addEventListener('click', () => {
      this.seatMirrorsAdjusted = true;
      if (this.onOpenCockpitAdjusterCallback) this.onOpenCockpitAdjusterCallback();
    });
    rightGroup.appendChild(btnCockpit);

    // 3D Trajectory Guide Button (🧭 Ideallinie [T])
    const btnTrajectory = document.createElement('button');
    btnTrajectory.id = 'hud-btn-trajectory-guide';
    btnTrajectory.innerHTML = `🧭 <span style="font-size:12px;font-weight:700;margin-left:4px;">Ideallinie [T]</span>`;
    btnTrajectory.title = '3D-Fahrstreifen-Ideallinie ein-/ausblenden [T]';
    btnTrajectory.style.cssText = `
      background: rgba(15, 23, 42, 0.9);
      border: 1.5px solid #10b981;
      color: #34d399;
      padding: 0 14px;
      height: 42px;
      border-radius: 21px;
      font-family: 'Chakra Petch', sans-serif;
      font-size: 14px;
      cursor: pointer;
      display: flex;
      align-items: center;
      box-shadow: 0 4px 14px rgba(16, 185, 129, 0.3);
      transition: background 0.15s, transform 0.1s;
      pointer-events: auto;
      touch-action: manipulation;
    `;
    btnTrajectory.addEventListener('click', () => {
      if (this.onToggleTrajectoryCallback) this.onToggleTrajectoryCallback();
    });
    rightGroup.appendChild(btnTrajectory);

    // Settings Quick Button
    const btnSettings = document.createElement('button');
    btnSettings.id = 'hud-btn-settings';
    btnSettings.innerHTML = '⚙️';
    btnSettings.title = 'Tastenbelegung & Einstellungen';
    btnSettings.style.cssText = `
      width: 42px;
      height: 42px;
      border-radius: 50%;
      background: rgba(15, 23, 42, 0.85);
      border: 1px solid #475569;
      color: white;
      font-size: 18px;
      cursor: pointer;
      display: flex;
      justify-content: center;
      align-items: center;
      pointer-events: auto;
      touch-action: manipulation;
    `;
    btnSettings.addEventListener('click', () => {
      if (this.onOpenSettingsCallback) this.onOpenSettingsCallback();
    });

    // ✕ Back to Menu Button
    const btnMenu = document.createElement('button');
    btnMenu.id = 'hud-btn-menu';
    btnMenu.innerHTML = '✕';
    btnMenu.title = 'Abbrechen (ESC)';
    btnMenu.style.cssText = `
      width: 42px;
      height: 42px;
      border-radius: 50%;
      background: rgba(127, 29, 29, 0.85);
      border: 1px solid #ef4444;
      color: #fca5a5;
      font-size: 16px;
      font-weight: bold;
      cursor: pointer;
      display: flex;
      justify-content: center;
      align-items: center;
      pointer-events: auto;
      touch-action: manipulation;
    `;
    btnMenu.addEventListener('click', () => {
      if (this.onReturnToMenuCallback) this.onReturnToMenuCallback();
    });

    // Top Bar button: Toggle Abfahrkontrolle checklist
    this.btnChecklistToggle = document.createElement('button');
    this.btnChecklistToggle.id = 'hud-btn-checklist-toggle';
    this.btnChecklistToggle.innerHTML = '📋 Check [K]';
    this.btnChecklistToggle.title = 'Abfahrkontrolle / Cockpit-Check ein-/ausblenden [K]';
    this.btnChecklistToggle.style.cssText = `
      background: rgba(30, 41, 59, 0.85);
      border: 1px solid #475569;
      color: #e2e8f0;
      padding: 6px 10px;
      border-radius: 8px;
      font-size: 12px;
      font-weight: 700;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 4px;
      pointer-events: auto;
      touch-action: manipulation;
      transition: all 0.2s ease;
    `;
    this.btnChecklistToggle.addEventListener('click', (e) => {
      (e.currentTarget as HTMLElement)?.blur();
      this.toggleChecklistCard();
    });
    rightGroup.appendChild(this.btnChecklistToggle);

    // StVO Theory Quiz Button (Fahrsimulator Edition)
    const btnTheoryQuiz = document.createElement('button');
    btnTheoryQuiz.id = 'hud-btn-theory-quiz';
    btnTheoryQuiz.innerHTML = '📚 <span style="font-size:12px;font-weight:700;">Theorie</span>';
    btnTheoryQuiz.title = 'StVO Theorie-Quiz (Fahrsimulator Fragen)';
    btnTheoryQuiz.style.cssText = `
      background: rgba(30, 41, 59, 0.85);
      border: 1px solid #3b82f6;
      color: #93c5fd;
      padding: 6px 10px;
      border-radius: 8px;
      font-size: 12px;
      font-weight: 700;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 4px;
      transition: all 0.2s ease;
      touch-action: manipulation;
    `;
    btnTheoryQuiz.addEventListener('click', (e) => {
      (e.currentTarget as HTMLElement)?.blur();
      this.toggleTheoryQuizCard();
    });
    rightGroup.appendChild(btnTheoryQuiz);

    // Top Bar button: Two-Way Orientation Toggle (Landscape <-> Portrait)
    const btnOrientation = document.createElement('button');
    btnOrientation.id = 'hud-btn-orientation-toggle';
    btnOrientation.style.cssText = `
      background: rgba(30, 41, 59, 0.85);
      border: 1px solid #475569;
      padding: 6px 10px;
      border-radius: 8px;
      font-size: 12px;
      font-weight: 700;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 4px;
      transition: all 0.2s ease;
      pointer-events: auto;
      touch-action: manipulation;
    `;

    const updateHudOrientationButton = () => {
      const isLandscape = window.innerWidth > window.innerHeight;
      btnOrientation.innerHTML = isLandscape ? '📱 Hochformat' : '🔄 Querformat';
      btnOrientation.title = isLandscape ? 'In Hochformat (Portrait) wechseln' : 'In Querformat (Landscape / Vollbild) wechseln';
      btnOrientation.style.color = isLandscape ? '#a78bfa' : '#38bdf8';
    };
    updateHudOrientationButton();
    window.addEventListener('resize', updateHudOrientationButton);
    window.addEventListener('orientationchange', updateHudOrientationButton);

    btnOrientation.addEventListener('click', async (e) => {
      (e.currentTarget as HTMLElement)?.blur();
      const isLandscape = window.innerWidth > window.innerHeight;
      try {
        if (!isLandscape) {
          // Switch to Landscape
          if (!document.fullscreenElement) {
            if (document.documentElement.requestFullscreen) {
              await document.documentElement.requestFullscreen().catch(() => {});
            } else if ((document.documentElement as any).webkitRequestFullscreen) {
              await (document.documentElement as any).webkitRequestFullscreen().catch(() => {});
            }
          }
          if (screen.orientation && (screen.orientation as any).lock) {
            await (screen.orientation as any).lock('landscape').catch(() => {});
          } else if ((screen as any).lockOrientation) {
            (screen as any).lockOrientation('landscape');
          }
        } else {
          // Switch back to Portrait
          if (screen.orientation && (screen.orientation as any).lock) {
            await (screen.orientation as any).lock('portrait').catch(() => {});
          } else if (screen.orientation && (screen.orientation as any).unlock) {
            (screen.orientation as any).unlock();
          } else if ((screen as any).unlockOrientation) {
            (screen as any).unlockOrientation();
          }
          if (document.fullscreenElement) {
            if (document.exitFullscreen) {
              await document.exitFullscreen().catch(() => {});
            } else if ((document as any).webkitExitFullscreen) {
              await (document as any).webkitExitFullscreen().catch(() => {});
            }
          }
        }
      } catch (err) {
        console.log('Orientation toggle note:', err);
      }
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
        updateHudOrientationButton();
      }, 200);
    });
    rightGroup.appendChild(btnOrientation);

    rightGroup.appendChild(btnSettings);
    rightGroup.appendChild(btnMenu);

    topRow.appendChild(leftGroup);
    topRow.appendChild(topAlertWrapper);
    topRow.appendChild(rightGroup);

    this.container.appendChild(topRow);

    // Build StVO Theory Quiz Card
    this.buildTheoryQuizCard(this.container);

    // 2. Middle Section: Stall Alert & Pre-Drive Checklist Card
    const middleSection = document.createElement('div');
    middleSection.id = 'hud-middle-section';
    middleSection.style.cssText = `display: flex; flex-direction: column; align-items: center; gap: 12px; pointer-events: none; width: 100%;`;

    // Stall Warning Alert
    this.stallAlert = document.createElement('div');
    this.stallAlert.style.cssText = `
      background: rgba(234, 179, 8, 0.95);
      color: #422006;
      padding: 12px 24px;
      border-radius: 12px;
      font-weight: 800;
      font-size: 18px;
      display: none;
      box-shadow: 0 6px 20px rgba(0,0,0,0.6);
      border: 2px solid #ca8a04;
      text-align: center;
      pointer-events: auto;
    `;
    this.stallAlert.innerHTML = `⚠️ MOTOR ABGEWÜRGT!<br><span style="font-size:13px;font-weight:600;">Kupplung treten &amp; Taste [I] drücken (Zündung / Motor anlassen)</span>`;
    middleSection.appendChild(this.stallAlert);

    // ── Pre-Drive Checklist (Abfahrkontrolle) Card ──────────────────────────
    this.buildPreDriveChecklistCard(middleSection);

    this.container.appendChild(middleSection);

    // 3. Bottom Section: Digital Dashboard Cluster
    const bottomContainer = document.createElement('div');
    bottomContainer.id = 'hud-bottom-container';
    bottomContainer.style.cssText = `display: flex; flex-direction: column; align-items: center; gap: 8px;`;

    // Virtual Cockpit Canvas Dashboard (Dual Analogue Gauges & Bordcomputer)
    this.dashboardCluster.canvas.id = 'hud-dashboard-canvas';
    bottomContainer.appendChild(this.dashboardCluster.canvas);

    // Quick Interactive Buttons Row (Clickable Lamps for Mobile / Desktop shortcuts)
    const quickControls = document.createElement('div');
    quickControls.id = 'hud-quick-controls';
    quickControls.style.cssText = `
      display: flex;
      align-items: center;
      gap: 12px;
      background: rgba(15, 23, 42, 0.85);
      backdrop-filter: blur(8px);
      border: 1px solid rgba(255, 255, 255, 0.12);
      border-radius: 12px;
      padding: 6px 14px;
      pointer-events: auto;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
    `;

    // Blinker Left
    this.blinkerL = document.createElement('div');
    this.blinkerL.innerHTML = '◀ Blinker L [Q]';
    this.blinkerL.title = 'Blinker Links (Taste Q / Y)';
    this.blinkerL.style.cssText = `
      font-size: 13px;
      font-weight: 800;
      color: #94a3b8;
      padding: 5px 12px;
      border-radius: 8px;
      background: rgba(30, 41, 59, 0.6);
      border: 1.5px solid #475569;
      cursor: pointer;
      user-select: none;
      transition: all 0.15s ease;
    `;
    this.blinkerL.addEventListener('click', () => {
      if (this.onBlinkerLeftCallback) this.onBlinkerLeftCallback();
    });

    // Seatbelt Lamp (Clickable)
    this.seatbeltLamp = document.createElement('div');
    this.seatbeltLamp.innerHTML = '🔒 Gurt [B]';
    this.seatbeltLamp.title = 'Sicherheitsgurt an-/abschnallen (Taste B)';
    this.seatbeltLamp.style.cssText = `
      font-size: 12px;
      font-weight: 700;
      color: #ef4444;
      padding: 4px 10px;
      border-radius: 6px;
      background: rgba(239, 68, 68, 0.2);
      border: 1px solid #ef4444;
      cursor: pointer;
      transition: all 0.2s;
    `;
    this.seatbeltLamp.addEventListener('click', () => {
      if (this.onToggleSeatbeltCallback) this.onToggleSeatbeltCallback();
    });

    // Handbrake Lamp (Clickable)
    this.handbrakeLamp = document.createElement('div');
    this.handbrakeLamp.innerHTML = '🅿️ Handbremse [Space]';
    this.handbrakeLamp.title = 'Handbremse lösen / anziehen (Leertaste)';
    this.handbrakeLamp.style.cssText = `
      font-size: 12px;
      font-weight: 700;
      color: #ef4444;
      padding: 4px 10px;
      border-radius: 6px;
      background: rgba(239, 68, 68, 0.2);
      border: 1px solid #ef4444;
      cursor: pointer;
      transition: all 0.2s;
    `;
    this.handbrakeLamp.addEventListener('click', () => {
      if (this.onToggleHandbrakeCallback) this.onToggleHandbrakeCallback();
    });

    // Engine Ignition Lamp (Clickable)
    this.engineLamp = document.createElement('div');
    this.engineLamp.innerHTML = '🔑 Motor [I]';
    this.engineLamp.title = 'Motor anlassen / stoppen (Taste I)';
    this.engineLamp.style.cssText = `
      font-size: 12px;
      font-weight: 700;
      color: #eab308;
      padding: 4px 10px;
      border-radius: 6px;
      background: rgba(234, 179, 8, 0.2);
      border: 1px solid #eab308;
      cursor: pointer;
      transition: all 0.2s;
    `;
    this.engineLamp.addEventListener('click', () => {
      if (this.onToggleEngineCallback) this.onToggleEngineCallback();
    });

    // Blinker Right
    this.blinkerR = document.createElement('div');
    this.blinkerR.innerHTML = 'Blinker R [E] ▶';
    this.blinkerR.title = 'Blinker Rechts (Taste E / C)';
    this.blinkerR.style.cssText = `
      font-size: 13px;
      font-weight: 800;
      color: #94a3b8;
      padding: 5px 12px;
      border-radius: 8px;
      background: rgba(30, 41, 59, 0.6);
      border: 1.5px solid #475569;
      cursor: pointer;
      user-select: none;
      transition: all 0.15s ease;
    `;
    this.blinkerR.addEventListener('click', () => {
      if (this.onBlinkerRightCallback) this.onBlinkerRightCallback();
    });

    // Dummy text holders
    this.speedText = document.createElement('span');
    this.gearText = document.createElement('span');
    this.rpmText = document.createElement('span');
    this.clockText = document.createElement('span');

    // Gear Button in bottom bar
    this.gearButton = document.createElement('div');
    this.gearButton.id = 'hud-quick-gear';
    this.gearButton.innerHTML = '⚙️ Gang: P [R/G]';
    this.gearButton.title = 'Fahrstufe auswählen (P / R / N / D) [Taste R/G]';
    this.gearButton.style.cssText = `
      font-size: 12px;
      font-weight: 700;
      color: #fbbf24;
      padding: 4px 10px;
      border-radius: 6px;
      background: rgba(245, 158, 11, 0.2);
      border: 1px solid #f59e0b;
      cursor: pointer;
      transition: all 0.2s;
    `;
    this.gearButton.addEventListener('click', () => {
      if (this.onSelectDriveGearCallback) this.onSelectDriveGearCallback();
    });

    // Speed indicator in bottom bar
    this.speedBadgeBottom = document.createElement('div');
    this.speedBadgeBottom.id = 'hud-quick-speed';
    this.speedBadgeBottom.innerHTML = '⚡ 0 km/h';
    this.speedBadgeBottom.title = 'Momentangeschwindigkeit';
    this.speedBadgeBottom.style.cssText = `
      font-size: 12px;
      font-weight: 700;
      color: #38bdf8;
      padding: 4px 10px;
      border-radius: 6px;
      background: rgba(56, 189, 248, 0.15);
      border: 1px solid rgba(56, 189, 248, 0.4);
      font-family: 'Chakra Petch', monospace;
    `;

    // Mouse Steering Toggle / Indicator Button in bottom bar
    this.btnMouseSteer = document.createElement('div');
    this.btnMouseSteer.id = 'hud-quick-mouse-steer';
    this.btnMouseSteer.innerHTML = '🖱️ Maus [M]';
    this.btnMouseSteer.title = 'Maus-Lenkung umschalten (Taste M / Ziehen mit der Maus)';
    this.btnMouseSteer.style.cssText = `
      font-size: 12px;
      font-weight: 700;
      color: #94a3b8;
      padding: 4px 10px;
      border-radius: 6px;
      background: rgba(30, 41, 59, 0.6);
      border: 1px solid #475569;
      cursor: pointer;
      user-select: none;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      gap: 4px;
    `;
    this.btnMouseSteer.addEventListener('click', () => {
      if (this.onToggleMouseSteeringCallback) this.onToggleMouseSteeringCallback();
    });

    // HORI Steering Wheel & Detachable Pedals Live Telemetry Toggle Button
    this.btnWheelTelemetry = document.createElement('div');
    this.btnWheelTelemetry.id = 'hud-btn-wheel-telemetry';
    this.btnWheelTelemetry.innerHTML = '🏎️ HORI Lenkrad';
    this.btnWheelTelemetry.title = 'Live Lenkrad & Abnehmbare Gas-/Bremspedale Telemetrie anzeigen';
    this.btnWheelTelemetry.style.cssText = `
      font-size: 12px;
      font-weight: 700;
      color: #fca5a5;
      padding: 4px 10px;
      border-radius: 6px;
      background: rgba(153, 27, 27, 0.35);
      border: 1px solid #ef4444;
      cursor: pointer;
      user-select: none;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      gap: 4px;
    `;
    this.btnWheelTelemetry.addEventListener('click', () => {
      this.toggleWheelTelemetryCard();
    });

    // Examiner Talk-Back / TTS Toggle Button (OFF by default — leaves only Directions & Mistakes display)
    this.btnTalkBackToggle = document.createElement('div');
    this.btnTalkBackToggle.id = 'hud-btn-talkback-toggle';
    this.btnTalkBackToggle.innerHTML = '🔇 Talk-Back: AUS [U]';
    this.btnTalkBackToggle.setAttribute('data-talkback', 'off');
    this.btnTalkBackToggle.title = 'Prüfer Talk-Back / Sprachausgabe (TTS) ein-/ausschalten [Taste U] — Standardmäßig AUS (nur Richtungs- & Fehleranzeige)';
    this.btnTalkBackToggle.style.cssText = `
      font-size: 12px;
      font-weight: 700;
      color: #94a3b8;
      padding: 4px 10px;
      border-radius: 6px;
      background: rgba(30, 41, 59, 0.65);
      border: 1px solid #475569;
      cursor: pointer;
      user-select: none;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      gap: 4px;
    `;
    this.btnTalkBackToggle.addEventListener('click', () => {
      this.setTalkBackEnabled(!this.talkBackEnabled);
      if (this.onToggleTalkBackCallback) {
        this.onToggleTalkBackCallback(this.talkBackEnabled);
      }
    });

    quickControls.appendChild(this.blinkerL);
    quickControls.appendChild(this.gearButton);
    quickControls.appendChild(this.speedBadgeBottom);
    quickControls.appendChild(this.btnMouseSteer);
    quickControls.appendChild(this.btnWheelTelemetry);
    quickControls.appendChild(this.btnTalkBackToggle);
    quickControls.appendChild(this.seatbeltLamp);
    quickControls.appendChild(this.handbrakeLamp);
    quickControls.appendChild(this.engineLamp);
    quickControls.appendChild(this.blinkerR);

    bottomContainer.appendChild(quickControls);
    this.container.appendChild(bottomContainer);

    // ── Score Panel (fixed right side, always visible during driving) ──────
    this.buildScorePanel();

    // ── HORI Steering Wheel & Detachable Pedals Telemetry Widget ─────────
    this.buildWheelTelemetryCard();

    // ── Shoulder-check vignette overlay ──────────────────────────────────
    this.shoulderVignette = document.createElement('div');
    this.shoulderVignette.style.cssText = `
      position: absolute; top: 0; left: 0; width: 100%; height: 100%;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.15s ease;
      background: transparent;
      z-index: 8;
    `;
    this.container.appendChild(this.shoulderVignette);

    // Direction label
    this.shoulderLabel = document.createElement('div');
    this.shoulderLabel.style.cssText = `
      position: absolute; top: 50%; transform: translateY(-50%);
      background: rgba(59, 130, 246, 0.85);
      color: white;
      font-family: 'Chakra Petch', sans-serif;
      font-size: 13px;
      font-weight: 700;
      padding: 6px 12px;
      border-radius: 8px;
      border: 1.5px solid #93c5fd;
      box-shadow: 0 2px 12px rgba(0,0,0,0.5);
      opacity: 0;
      transition: opacity 0.15s ease;
      pointer-events: none;
      letter-spacing: 0.05em;
      z-index: 9;
    `;
    this.container.appendChild(this.shoulderLabel);
  }

  private buildPreDriveChecklistCard(parent: HTMLElement) {
    // ── Checklist Backdrop Overlay (Dusky dark overlay for dismissal) ─────
    this.checklistBackdrop = document.createElement('div');
    this.checklistBackdrop.id = 'hud-checklist-backdrop';
    this.checklistBackdrop.style.cssText = `
      position: fixed;
      inset: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(10, 15, 28, 0.75);
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
      z-index: 9998;
      pointer-events: auto;
      opacity: 1;
      display: block;
      transition: opacity 0.25s ease;
    `;
    const dismissViaBackdrop = (e: Event) => {
      e.stopPropagation();
      this.toggleChecklistCard(false);
    };
    this.checklistBackdrop.addEventListener('click', dismissViaBackdrop);
    this.checklistBackdrop.addEventListener('pointerdown', dismissViaBackdrop);
    this.container.appendChild(this.checklistBackdrop);

    this.checklistCard = document.createElement('div');
    this.checklistCard.id = 'hud-predrive-checklist';
    this.checklistCard.style.cssText = `
      background: rgba(15, 23, 42, 0.95);
      backdrop-filter: blur(14px);
      -webkit-backdrop-filter: blur(14px);
      border: 2px solid #3b82f6;
      border-radius: 16px;
      padding: 12px 16px;
      display: flex;
      flex-direction: column;
      gap: 8px;
      width: 440px;
      max-width: 94vw;
      max-height: min(85vh, 480px);
      overflow-y: auto;
      overflow-x: hidden;
      -webkit-overflow-scrolling: touch;
      touch-action: pan-y;
      overscroll-behavior: contain;
      scrollbar-width: thin;
      scrollbar-color: #3b82f6 rgba(15, 23, 42, 0.5);
      box-shadow: 0 12px 36px rgba(0, 0, 0, 0.8);
      pointer-events: auto;
      z-index: 9999;
      box-sizing: border-box;
      transition: all 0.3s ease;
    `;

    // Mobile-specific style injection (runs once)
    if (!document.getElementById('hud-checklist-mobile-css')) {
      const mobileStyle = document.createElement('style');
      mobileStyle.id = 'hud-checklist-mobile-css';
      mobileStyle.textContent = `
        #hud-predrive-checklist::-webkit-scrollbar,
        #hud-checklist-items::-webkit-scrollbar {
          width: 5px;
        }
        #hud-predrive-checklist::-webkit-scrollbar-track,
        #hud-checklist-items::-webkit-scrollbar-track {
          background: rgba(15, 23, 42, 0.6);
          border-radius: 4px;
        }
        #hud-predrive-checklist::-webkit-scrollbar-thumb,
        #hud-checklist-items::-webkit-scrollbar-thumb {
          background: #3b82f6;
          border-radius: 4px;
        }
        #hud-predrive-checklist::-webkit-scrollbar-thumb:hover,
        #hud-checklist-items::-webkit-scrollbar-thumb:hover {
          background: #60a5fa;
        }

        @media (max-width: 900px), (max-height: 550px) {
          #hud-predrive-checklist {
            position: fixed !important;
            top: 50% !important;
            left: 50% !important;
            transform: translate(-50%, -50%) !important;
            width: min(440px, 92vw) !important;
            max-width: 92vw !important;
            max-height: min(88vh, 330px) !important;
            margin: 0 !important;
            z-index: 9999 !important;
            padding: 10px 14px !important;
            gap: 6px !important;
            border-radius: 14px !important;
            font-size: 11px !important;
            overflow-y: auto !important;
            overflow-x: hidden !important;
            touch-action: pan-y !important;
            -webkit-overflow-scrolling: touch !important;
            overscroll-behavior: contain !important;
            box-shadow: 0 16px 48px rgba(0, 0, 0, 0.9), 0 0 0 1.5px rgba(59, 130, 246, 0.5) !important;
          }
          #hud-checklist-items {
            gap: 4px !important;
            overflow-y: auto !important;
            touch-action: pan-y !important;
            -webkit-overflow-scrolling: touch !important;
            overscroll-behavior: contain !important;
            flex: 1 1 auto !important;
            min-height: 70px !important;
            max-height: 180px !important;
          }
          #hud-checklist-items > div {
            padding: 4px 8px !important;
            flex-shrink: 0 !important;
            touch-action: pan-y !important;
          }
          #hud-predrive-checklist button {
            padding: 4px 8px !important;
            font-size: 11px !important;
          }
          #hud-checklist-actions {
            display: flex !important;
            gap: 6px !important;
            width: 100% !important;
            flex-shrink: 0 !important;
          }
          #hud-checklist-confirm-all {
            padding: 8px 10px !important;
            font-size: 11px !important;
            flex: 1 !important;
            flex-shrink: 0 !important;
            touch-action: manipulation !important;
          }
          #hud-checklist-close-bottom {
            padding: 8px 10px !important;
            font-size: 11px !important;
            flex-shrink: 0 !important;
            touch-action: manipulation !important;
          }
        }
        @media (max-height: 480px) {
          #hud-predrive-checklist {
            max-height: min(88vh, 310px) !important;
            padding: 6px 10px !important;
            gap: 4px !important;
          }
          #hud-checklist-items {
            gap: 3px !important;
            max-height: 155px !important;
          }
          #hud-checklist-items > div {
            padding: 3px 6px !important;
          }
          #hud-checklist-confirm-all,
          #hud-checklist-close-bottom {
            padding: 6px 8px !important;
            font-size: 11px !important;
          }
        }
      `;
      document.head.appendChild(mobileStyle);
    }

    const cardHeader = document.createElement('div');
    cardHeader.style.cssText = `display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid rgba(255,255,255,0.1); padding-bottom: 8px; flex-shrink: 0;`;

    const titleBox = document.createElement('div');
    titleBox.style.cssText = `display: flex; flex-direction: column; gap: 2px;`;

    const titleText = document.createElement('div');
    titleText.style.cssText = `font-size: 13px; font-weight: 800; color: #38bdf8; display: flex; align-items: center; gap: 6px;`;
    titleText.innerHTML = `<span>📋</span><span>ABFAHRKONTROLLE VOR FAHRTBEGINN</span>`;

    const hintText = document.createElement('div');
    hintText.style.cssText = `font-size: 11px; color: #94a3b8; font-weight: 500;`;
    hintText.innerHTML = `StVO Cockpit-Check: Tasten <b>[B]</b>, <b>[I]</b>, <b>[Space]</b>, <b>[R]</b> oder Buttons nutzen.`;

    titleBox.appendChild(titleText);
    titleBox.appendChild(hintText);

    const btnMinimize = document.createElement('button');
    btnMinimize.innerText = '−';
    btnMinimize.title = 'Minimieren / Erweitern';
    btnMinimize.style.cssText = `
      background: rgba(51, 65, 85, 0.7);
      border: 1px solid #64748b;
      color: #f8fafc;
      border-radius: 6px;
      width: 26px;
      height: 26px;
      font-size: 14px;
      font-weight: bold;
      cursor: pointer;
      display: flex;
      justify-content: center;
      align-items: center;
    `;
    btnMinimize.id = 'hud-btn-checklist-minimize';
    btnMinimize.addEventListener('click', (e) => {
      (e.currentTarget as HTMLElement)?.blur();
      const isCollapsed = this.checklistCard.getAttribute('data-collapsed') === 'true';
      if (isCollapsed) {
        this.checklistCard.setAttribute('data-collapsed', 'false');
        btnMinimize.innerText = '−';
        itemsList.style.display = 'flex';
      } else {
        this.checklistCard.setAttribute('data-collapsed', 'true');
        btnMinimize.innerText = '+';
        itemsList.style.display = 'none';
      }
    });

    const btnClose = document.createElement('button');
    btnClose.id = 'hud-btn-checklist-close';
    btnClose.innerText = '✕';
    btnClose.title = 'Schließen (Straße & Windschutzscheibe freigeben) [K]';
    btnClose.style.cssText = `
      background: rgba(239, 68, 68, 0.35);
      border: 2px solid #ef4444;
      color: #ffffff;
      border-radius: 8px;
      width: 36px;
      height: 36px;
      min-width: 36px;
      min-height: 36px;
      font-size: 16px;
      font-weight: 800;
      cursor: pointer;
      display: flex;
      justify-content: center;
      align-items: center;
      touch-action: manipulation;
      pointer-events: auto;
      transition: background 0.15s ease, transform 0.1s ease;
    `;
    btnClose.addEventListener('click', (e) => {
      (e.currentTarget as HTMLElement)?.blur();
      this.toggleChecklistCard(false);
    });

    const headerBtnsGroup = document.createElement('div');
    headerBtnsGroup.style.cssText = `display: flex; align-items: center; gap: 6px;`;
    headerBtnsGroup.appendChild(btnMinimize);
    headerBtnsGroup.appendChild(btnClose);

    cardHeader.appendChild(titleBox);
    cardHeader.appendChild(headerBtnsGroup);
    this.checklistCard.appendChild(cardHeader);

    const itemsList = document.createElement('div');
    itemsList.id = 'hud-checklist-items';
    itemsList.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 6px;
      overflow-y: auto;
      touch-action: pan-y;
      -webkit-overflow-scrolling: touch;
      overscroll-behavior: contain;
      scrollbar-width: thin;
      scrollbar-color: #3b82f6 rgba(15, 23, 42, 0.5);
      flex: 1 1 auto;
      min-height: 80px;
      padding-right: 2px;
    `;

    const checklistConfigs = [
      { id: 'seatMirrors', label: '🪑 1. Sitz & Spiegel einstellen', actionText: 'Anpassen 🪞', actionCb: () => {
        this.seatMirrorsAdjusted = true;
        if (this.onOpenCockpitAdjusterCallback) this.onOpenCockpitAdjusterCallback();
      }},
      { id: 'seatbelt', label: '🔒 2. Sicherheitsgurt anlegen', actionText: 'Anschnallen [B]', actionCb: () => {
        if (this.onToggleSeatbeltCallback) this.onToggleSeatbeltCallback();
      }},
      { id: 'engine', label: '🔑 3. Motor anlassen (Zündung)', actionText: 'Starten [I]', actionCb: () => {
        if (this.onToggleEngineCallback) this.onToggleEngineCallback();
      }},
      { id: 'handbrake', label: '🅿️ 4. Handbremse lösen', actionText: 'Lösen [Space]', actionCb: () => {
        // Release-only: clicking "Lösen" always releases the handbrake, never re-engages it.
        // Using onReleaseHandbrakeCallback prevents the common UX mistake of double-clicking the
        // toggle and accidentally re-engaging the handbrake before driving.
        if (this.onReleaseHandbrakeCallback) this.onReleaseHandbrakeCallback();
      }},
      { id: 'gear', label: '🕹️ 5. Fahrstufe D / Gang 1', actionText: 'D einlegen [R]', actionCb: () => {
        if (this.onSelectDriveGearCallback) this.onSelectDriveGearCallback();
      }},
    ];

    checklistConfigs.forEach(({ id, label, actionText, actionCb }) => {
      const row = document.createElement('div');
      row.style.cssText = `
        display: flex;
        justify-content: space-between;
        align-items: center;
        background: rgba(30, 41, 59, 0.7);
        border: 1px solid rgba(51, 65, 85, 0.8);
        border-radius: 8px;
        padding: 6px 10px;
        flex-shrink: 0;
        touch-action: pan-y;
      `;

      const lbl = document.createElement('span');
      lbl.style.cssText = `font-size: 12px; color: #e2e8f0; font-weight: 600; touch-action: pan-y;`;
      lbl.innerText = label;

      const rightBox = document.createElement('div');
      rightBox.style.cssText = `display: flex; align-items: center; gap: 8px;`;

      const statusEl = document.createElement('span');
      statusEl.style.cssText = `font-size: 11px; font-weight: 700; color: #ef4444; touch-action: pan-y;`;
      statusEl.innerText = '⭕ Ausstehend';

      let btnAction: HTMLButtonElement | undefined;
      if (actionCb) {
        btnAction = document.createElement('button');
        btnAction.innerText = actionText;
        btnAction.style.cssText = `
          background: #2563eb;
          border: 1px solid #3b82f6;
          color: white;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 700;
          cursor: pointer;
          touch-action: manipulation;
        `;
        btnAction.addEventListener('click', (e) => {
          (e.currentTarget as HTMLElement)?.blur();
          actionCb();
        });
        rightBox.appendChild(btnAction);
      }

      rightBox.appendChild(statusEl);
      row.appendChild(lbl);
      row.appendChild(rightBox);
      itemsList.appendChild(row);

      this.checklistItems.set(id, { row, statusEl, btnAction });
    });

    this.checklistCard.appendChild(itemsList);

    // ── Actions Container (Quick Confirm + Bottom Close Button) ───────────
    const actionsContainer = document.createElement('div');
    actionsContainer.id = 'hud-checklist-actions';
    actionsContainer.style.cssText = `
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      margin-top: 4px;
      flex-shrink: 0;
    `;

    const confirmAllBtn = document.createElement('button');
    confirmAllBtn.id = 'hud-checklist-confirm-all';
    confirmAllBtn.innerHTML = '🚀 Schnellstart (Alles erledigt &amp; Losfahren)';
    confirmAllBtn.style.cssText = `
      flex: 1;
      padding: 10px 14px;
      background: linear-gradient(135deg, #15803d 0%, #166534 100%);
      border: 2px solid #22c55e;
      border-radius: 10px;
      color: white;
      font-size: 12px;
      font-weight: 800;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      letter-spacing: 0.2px;
      box-shadow: 0 4px 16px rgba(34, 197, 94, 0.3);
      pointer-events: auto;
      touch-action: manipulation;
      flex-shrink: 0;
      white-space: nowrap;
    `;
    confirmAllBtn.addEventListener('click', (e) => {
      (e.currentTarget as HTMLElement)?.blur();
      this.confirmAllPreDriveChecks();
    });

    const closeBtnBottom = document.createElement('button');
    closeBtnBottom.id = 'hud-checklist-close-bottom';
    closeBtnBottom.innerText = '✕ Schließen';
    closeBtnBottom.innerHTML = '✕ Schließen';
    closeBtnBottom.title = 'Abfahrkontrolle schließen';
    closeBtnBottom.style.cssText = `
      padding: 10px 14px;
      background: rgba(51, 65, 85, 0.85);
      border: 1.5px solid #64748b;
      border-radius: 10px;
      color: #f1f5f9;
      font-size: 12px;
      font-weight: 700;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      pointer-events: auto;
      touch-action: manipulation;
      flex-shrink: 0;
      white-space: nowrap;
      transition: background 0.15s ease;
    `;
    closeBtnBottom.addEventListener('click', (e) => {
      (e.currentTarget as HTMLElement)?.blur();
      this.toggleChecklistCard(false);
    });

    actionsContainer.appendChild(confirmAllBtn);
    actionsContainer.appendChild(closeBtnBottom);
    this.checklistCard.appendChild(actionsContainer);

    // Programmatic touch drag scrolling fallback for mobile & touch viewports
    const attachTouchScroll = (el: HTMLElement) => {
      let startY = 0;
      let startScrollTop = 0;
      let isDragging = false;
      el.addEventListener('touchstart', (e: any) => {
        const touch = e.touches ? e.touches[0] : e;
        if (touch) {
          startY = touch.clientY;
          startScrollTop = el.scrollTop;
          isDragging = true;
        }
      }, { passive: true });

      el.addEventListener('touchmove', (e: any) => {
        if (!isDragging) return;
        const touch = e.touches ? e.touches[0] : e;
        if (touch) {
          const deltaY = touch.clientY - startY;
          el.scrollTop = startScrollTop - deltaY;
        }
      }, { passive: true });

      const stopDrag = () => { isDragging = false; };
      el.addEventListener('touchend', stopDrag, { passive: true });
      el.addEventListener('touchcancel', stopDrag, { passive: true });

      el.addEventListener('wheel', (e: any) => {
        el.scrollTop += e.deltaY;
      }, { passive: true });
    };

    attachTouchScroll(itemsList);
    attachTouchScroll(this.checklistCard);

    parent.appendChild(this.checklistCard);
  }

  public confirmAllPreDriveChecks() {
    this.seatMirrorsAdjusted = true;
    if (this.onOpenCockpitAdjusterCallback) {
      this.seatMirrorsAdjusted = true;
    }
    if (this.onFastenSeatbeltCallback) {
      this.onFastenSeatbeltCallback();
    } else if (this.onToggleSeatbeltCallback) {
      this.onToggleSeatbeltCallback();
    }
    if (this.onStartEngineCallback) {
      this.onStartEngineCallback();
    } else if (this.onToggleEngineCallback) {
      this.onToggleEngineCallback();
    }
    if (this.onReleaseHandbrakeCallback) {
      this.onReleaseHandbrakeCallback();
    }
    if (this.onSelectDriveGearCallback) {
      this.onSelectDriveGearCallback();
    }
    this.checklistItems.forEach(({ statusEl, btnAction }, id) => {
      statusEl.innerText = '✅ Erledigt';
      statusEl.style.color = '#22c55e';
      if (btnAction) {
        if (id === 'seatMirrors') {
          btnAction.style.display = 'inline-block';
          btnAction.innerText = 'Ändern 🪑';
          btnAction.style.background = '#15803d';
          btnAction.style.borderColor = '#22c55e';
        } else {
          btnAction.style.display = 'none';
        }
      }
    });
    this.preDriveCompleted = true;
    this.toggleChecklistCard(false);
  }

  private buildTheoryQuizCard(parent: HTMLElement) {
    this.theoryQuizCard = document.createElement('div');
    this.theoryQuizCard.id = 'hud-theory-quiz-card';
    this.theoryQuizCard.style.cssText = `
      position: absolute;
      top: clamp(54px, 10vh, 76px);
      left: clamp(8px, 2vw, 20px);
      width: min(320px, 92vw);
      background: rgba(15, 23, 42, 0.94);
      backdrop-filter: blur(16px);
      border: 1.5px solid #3b82f6;
      border-radius: 14px;
      padding: clamp(10px, 2.5vw, 14px);
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.75);
      display: flex;
      flex-direction: column;
      gap: 10px;
      pointer-events: auto;
      z-index: 35;
      box-sizing: border-box;
      transform: translateX(-20px);
      opacity: 0;
      display: none;
      transition: transform 0.22s ease, opacity 0.22s ease;
    `;

    // 1. Header
    const cardHeader = document.createElement('div');
    cardHeader.style.cssText = `display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid rgba(255,255,255,0.1); padding-bottom: 6px;`;

    const titleBox = document.createElement('div');
    titleBox.style.cssText = `display: flex; align-items: center; gap: 6px;`;
    titleBox.innerHTML = `
      <span style="font-size: 16px;">📚</span>
      <div>
        <div style="font-size: 12px; font-weight: 800; color: #f8fafc; font-family: 'Chakra Petch', sans-serif; letter-spacing: 0.05em;">STVO THEORIE-QUIZ</div>
        <div style="font-size: 9px; color: #94a3b8; font-weight: 600;">Fahrsimulator Praxisfragen</div>
      </div>
    `;

    const headerBtns = document.createElement('div');
    headerBtns.style.cssText = `display: flex; gap: 4px; align-items: center;`;

    const btnNext = document.createElement('button');
    btnNext.innerText = '⏭️';
    btnNext.title = 'Nächste Frage';
    btnNext.style.cssText = `background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.2); color: #f8fafc; border-radius: 6px; width: 30px; height: 30px; font-size: 12px; cursor: pointer; display: flex; align-items: center; justify-content: center; touch-action: manipulation;`;
    btnNext.addEventListener('click', () => this.loadNextTheoryQuestion());

    const btnClose = document.createElement('button');
    btnClose.innerText = '✕';
    btnClose.title = 'Schließen';
    btnClose.style.cssText = `background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.2); color: #f8fafc; border-radius: 6px; width: 30px; height: 30px; font-size: 14px; font-weight: bold; cursor: pointer; display: flex; align-items: center; justify-content: center; touch-action: manipulation;`;
    btnClose.addEventListener('click', () => this.toggleTheoryQuizCard());

    headerBtns.appendChild(btnNext);
    headerBtns.appendChild(btnClose);
    cardHeader.appendChild(titleBox);
    cardHeader.appendChild(headerBtns);
    this.theoryQuizCard.appendChild(cardHeader);

    // Context Badge
    this.quizContextBadge = document.createElement('div');
    this.quizContextBadge.style.cssText = `font-size: 10px; font-weight: 700; color: #38bdf8; background: rgba(56, 189, 248, 0.12); border: 1px solid rgba(56, 189, 248, 0.3); border-radius: 6px; padding: 2px 6px; width: fit-content;`;
    this.theoryQuizCard.appendChild(this.quizContextBadge);

    // Question Title
    this.quizTitleEl = document.createElement('div');
    this.quizTitleEl.style.cssText = `font-size: 12px; font-weight: 700; color: #f1f5f9; line-height: 1.4;`;
    this.theoryQuizCard.appendChild(this.quizTitleEl);

    // Options Container
    this.quizOptionsContainer = document.createElement('div');
    this.quizOptionsContainer.style.cssText = `display: flex; flex-direction: column; gap: 6px;`;
    this.theoryQuizCard.appendChild(this.quizOptionsContainer);

    // Submit Button
    this.btnQuizSubmit = document.createElement('button');
    this.btnQuizSubmit.innerText = '✓ Antwort prüfen';
    this.btnQuizSubmit.style.cssText = `
      background: #2563eb;
      border: 1px solid #3b82f6;
      color: white;
      padding: 8px 12px;
      border-radius: 8px;
      font-size: 12px;
      font-weight: 700;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      touch-action: manipulation;
      transition: all 0.15s;
    `;
    this.btnQuizSubmit.addEventListener('click', () => this.submitQuizAnswer());
    this.theoryQuizCard.appendChild(this.btnQuizSubmit);

    // Feedback Element
    this.quizFeedbackEl = document.createElement('div');
    this.quizFeedbackEl.style.cssText = `font-size: 11px; line-height: 1.35; padding: 8px; border-radius: 8px; display: none;`;
    this.theoryQuizCard.appendChild(this.quizFeedbackEl);

    parent.appendChild(this.theoryQuizCard);
  }

  public toggleTheoryQuizCard() {
    if (!this.theoryQuizCard) return;
    const isVisible = this.theoryQuizCard.style.display === 'flex';
    if (isVisible) {
      this.theoryQuizCard.style.opacity = '0';
      this.theoryQuizCard.style.transform = 'translateX(-20px)';
      setTimeout(() => {
        this.theoryQuizCard.style.display = 'none';
      }, 200);
    } else {
      if (!this.currentQuizQuestion) {
        this.loadNextTheoryQuestion();
      }
      this.theoryQuizCard.style.display = 'flex';
      requestAnimationFrame(() => {
        this.theoryQuizCard.style.opacity = '1';
        this.theoryQuizCard.style.transform = 'translateX(0)';
      });
    }
  }

  public loadNextTheoryQuestion() {
    const q = this.theoryQuizManager.getNextQuestion();
    if (!q) return;
    this.currentQuizQuestion = q;
    this.quizSelectedOption = -1;
    this.quizContextBadge.innerText = q.context;
    this.quizTitleEl.innerText = q.title;
    this.quizFeedbackEl.style.display = 'none';
    this.btnQuizSubmit.style.display = 'flex';
    this.btnQuizSubmit.disabled = false;

    this.quizOptionsContainer.innerHTML = '';
    q.options.forEach((opt, idx) => {
      const optRow = document.createElement('button');
      optRow.style.cssText = `
        background: rgba(30, 41, 59, 0.7);
        border: 1px solid rgba(51, 65, 85, 0.8);
        border-radius: 8px;
        padding: 6px 10px;
        color: #e2e8f0;
        font-size: 11px;
        font-weight: 600;
        text-align: left;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 6px;
        transition: all 0.15s;
        touch-action: manipulation;
      `;
      optRow.innerHTML = `<span style="color:#94a3b8;font-size:10px;">[${idx + 1}]</span> <span>${opt}</span>`;
      optRow.addEventListener('click', () => {
        this.quizSelectedOption = idx;
        Array.from(this.quizOptionsContainer.children).forEach((child, cIdx) => {
          (child as HTMLElement).style.background = cIdx === idx ? 'rgba(37, 99, 235, 0.4)' : 'rgba(30, 41, 59, 0.7)';
          (child as HTMLElement).style.borderColor = cIdx === idx ? '#3b82f6' : 'rgba(51, 65, 85, 0.8)';
          (child as HTMLElement).style.color = cIdx === idx ? '#ffffff' : '#e2e8f0';
        });
      });
      this.quizOptionsContainer.appendChild(optRow);
    });
  }

  public submitQuizAnswer() {
    if (!this.currentQuizQuestion || this.quizSelectedOption < 0) return;
    const res = this.theoryQuizManager.answerQuestion(this.currentQuizQuestion.id, this.quizSelectedOption);
    this.quizFeedbackEl.style.display = 'block';
    if (res.isCorrect) {
      this.quizFeedbackEl.style.background = 'rgba(22, 101, 52, 0.45)';
      this.quizFeedbackEl.style.border = '1px solid #22c55e';
      this.quizFeedbackEl.style.color = '#4ade80';
      this.quizFeedbackEl.innerHTML = `<strong>✅ Richtig! (+${res.points} Pkt)</strong><br>${res.explanation}`;
    } else {
      this.quizFeedbackEl.style.background = 'rgba(153, 27, 27, 0.45)';
      this.quizFeedbackEl.style.border = '1px solid #ef4444';
      this.quizFeedbackEl.style.color = '#fca5a5';
      this.quizFeedbackEl.innerHTML = `<strong>❌ Leider falsch!</strong><br>${res.explanation}`;
    }
    this.btnQuizSubmit.style.display = 'none';
  }

  private buildScorePanel() {
    if (!document.getElementById('hud-anim-style')) {
      const style = document.createElement('style');
      style.id = 'hud-anim-style';
      style.textContent = `
        @keyframes hudFlashIn {
          0%   { opacity: 0; transform: translateX(20px); background: rgba(239,68,68,0.35); }
          40%  { opacity: 1; transform: translateX(0);    background: rgba(239,68,68,0.22); }
          100% { background: transparent; }
        }
        .hud-infraction-new { animation: hudFlashIn 1.2s ease forwards; }

        @media (max-width: 900px), (max-height: 550px) {
          #hud-score-panel {
            display: none !important;
          }
        }
      `;
      document.head.appendChild(style);
    }

    this.scorePanel = document.createElement('div');
    this.scorePanel.id = 'hud-score-panel';
    this.scorePanel.style.cssText = `
      position: absolute;
      top: 70px;
      right: 14px;
      width: 190px;
      background: rgba(15, 23, 42, 0.88);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255,255,255,0.12);
      border-radius: 14px;
      padding: 10px 12px 8px;
      display: flex;
      flex-direction: column;
      gap: 6px;
      pointer-events: none;
      z-index: 16;
      box-shadow: 0 6px 24px rgba(0,0,0,0.55);
    `;

    const scoreHeader = document.createElement('div');
    scoreHeader.className = 'hud-score-header';
    scoreHeader.style.cssText = `display: flex; align-items: baseline; justify-content: space-between;`;
    const scoreLabel = document.createElement('span');
    scoreLabel.style.cssText = `font-family: 'Chakra Petch', sans-serif; font-size: 10px; font-weight: 700; color: #94a3b8; letter-spacing: 0.08em;`;
    scoreLabel.innerText = 'PRÜFUNGSPUNKTE';

    this.scoreValue = document.createElement('span');
    this.scoreValue.className = 'hud-score-val';
    this.scoreValue.style.cssText = `font-family: 'Chakra Petch', sans-serif; font-size: 26px; font-weight: 800; color: #22c55e; line-height: 1;`;
    this.scoreValue.innerText = '100';

    scoreHeader.appendChild(scoreLabel);
    scoreHeader.appendChild(this.scoreValue);
    this.scorePanel.appendChild(scoreHeader);

    const divider = document.createElement('div');
    divider.style.cssText = `height: 1px; background: rgba(255,255,255,0.1); margin: 2px 0;`;
    this.scorePanel.appendChild(divider);

    this.infractionList = document.createElement('div');
    this.infractionList.style.cssText = `
      display: flex; flex-direction: column; gap: 4px;
      max-height: 260px; overflow-y: auto;
      scrollbar-width: none;
    `;
    const placeholder = document.createElement('div');
    placeholder.id = 'hud-no-infraction-placeholder';
    placeholder.style.cssText = `font-size: 11px; color: #64748b; text-align: center; padding: 4px 0;`;
    placeholder.innerText = '✓ Noch keine Fehler';
    this.infractionList.appendChild(placeholder);
    this.scorePanel.appendChild(this.infractionList);

    this.container.appendChild(this.scorePanel);
  }

  private buildWheelTelemetryCard() {
    this.wheelTelemetryCard = document.createElement('div');
    this.wheelTelemetryCard.id = 'hud-wheel-telemetry-card';
    this.wheelTelemetryCard.setAttribute('data-visible', 'false');
    this.wheelTelemetryCard.style.cssText = `
      position: absolute;
      bottom: 66px;
      left: 16px;
      width: 230px;
      background: rgba(15, 23, 42, 0.90);
      backdrop-filter: blur(10px);
      border: 1.5px solid rgba(239, 68, 68, 0.65);
      border-radius: 14px;
      padding: 10px 12px;
      display: none;
      flex-direction: column;
      gap: 8px;
      pointer-events: auto;
      z-index: 22;
      box-shadow: 0 8px 28px rgba(0, 0, 0, 0.65);
    `;

    // Header
    const header = document.createElement('div');
    header.style.cssText = `display:flex;justify-content:space-between;align-items:center;gap:4px;`;
    header.innerHTML = `
      <div style="display:flex;align-items:center;gap:5px;">
        <span style="font-size:14px;">🏎️</span>
        <span style="font-family:'Chakra Petch',sans-serif;font-size:11px;font-weight:800;color:#f8fafc;letter-spacing:0.04em;">HORI PRO DX</span>
      </div>
    `;

    const rightControls = document.createElement('div');
    rightControls.style.cssText = `display:flex;align-items:center;gap:4px;`;

    this.wheelStatusBadge = document.createElement('span');
    this.wheelStatusBadge.id = 'hud-wheel-status-badge';
    this.wheelStatusBadge.style.cssText = `
      font-size: 9.5px;
      font-weight: 800;
      padding: 2px 6px;
      border-radius: 999px;
      background: rgba(16, 185, 129, 0.2);
      border: 1px solid #10b981;
      color: #34d399;
      font-family: monospace;
    `;
    this.wheelStatusBadge.innerText = '270°';

    const btnCalib = document.createElement('button');
    btnCalib.innerText = '⚙️';
    btnCalib.title = 'Lenkrad & Abnehmbare Pedale kalibrieren';
    btnCalib.style.cssText = `
      background: #1e293b; border: 1px solid #475569; color: #f8fafc;
      border-radius: 5px; width: 22px; height: 22px; font-size: 11px; cursor: pointer;
      display: flex; align-items: center; justify-content: center; padding: 0;
    `;
    btnCalib.addEventListener('click', () => {
      if (this.onOpenSettingsCallback) this.onOpenSettingsCallback();
    });

    const btnClose = document.createElement('button');
    btnClose.innerText = '✕';
    btnClose.title = 'Ausblenden';
    btnClose.style.cssText = `
      background: #1e293b; border: 1px solid #475569; color: #94a3b8;
      border-radius: 5px; width: 22px; height: 22px; font-size: 11px; cursor: pointer;
      display: flex; align-items: center; justify-content: center; padding: 0;
    `;
    btnClose.addEventListener('click', () => {
      this.toggleWheelTelemetryCard(false);
    });

    rightControls.appendChild(this.wheelStatusBadge);
    rightControls.appendChild(btnCalib);
    rightControls.appendChild(btnClose);
    header.appendChild(rightControls);
    this.wheelTelemetryCard.appendChild(header);

    // Center row: Left Brake Pedal Bar | Rotating HORI Wheel SVG | Right Gas Pedal Bar
    const gaugesRow = document.createElement('div');
    gaugesRow.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      background: rgba(9, 13, 22, 0.65);
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 10px;
      padding: 8px 10px;
    `;

    // Left Detachable Pedal: BREMSE (ZL)
    const brakeCol = document.createElement('div');
    brakeCol.style.cssText = `display:flex;flex-direction:column;align-items:center;gap:3px;width:44px;`;
    brakeCol.innerHTML = `<span style="font-size:9px;font-weight:800;color:#f87171;">🛑 BREMSE</span>`;
    const brakeTrack = document.createElement('div');
    brakeTrack.style.cssText = `
      width: 16px; height: 58px; background: #0f172a; border: 1px solid #475569;
      border-radius: 5px; overflow: hidden; display: flex; align-items: flex-end;
    `;
    this.wheelBrakeBar = document.createElement('div');
    this.wheelBrakeBar.id = 'hud-wheel-brake-bar';
    this.wheelBrakeBar.style.cssText = `
      width: 100%; height: 0%;
      background: linear-gradient(180deg, #ef4444 0%, #991b1b 100%);
      transition: height 0.04s linear;
    `;
    brakeTrack.appendChild(this.wheelBrakeBar);
    this.wheelBrakePct = document.createElement('span');
    this.wheelBrakePct.id = 'hud-wheel-brake-pct';
    this.wheelBrakePct.style.cssText = `font-family:monospace;font-size:10px;font-weight:700;color:#fca5a5;`;
    this.wheelBrakePct.innerText = '0%';
    brakeCol.appendChild(brakeTrack);
    brakeCol.appendChild(this.wheelBrakePct);

    // Center Rotating HORI Red/Black Steering Wheel
    const wheelCol = document.createElement('div');
    wheelCol.style.cssText = `display:flex;flex-direction:column;align-items:center;gap:4px;flex:1;`;
    this.wheelSvgRotor = document.createElement('div');
    this.wheelSvgRotor.id = 'hud-wheel-svg-rotor';
    this.wheelSvgRotor.style.cssText = `
      width: 68px; height: 68px;
      display: flex; align-items: center; justify-content: center;
      transition: transform 0.04s linear;
    `;
    this.wheelSvgRotor.innerHTML = `
      <svg width="68" height="68" viewBox="0 0 100 100">
        <!-- L/R Shift Paddles -->
        <rect x="14" y="34" width="10" height="22" rx="3" fill="#334155" stroke="#94a3b8" stroke-width="1.5"/>
        <rect x="76" y="34" width="10" height="22" rx="3" fill="#334155" stroke="#94a3b8" stroke-width="1.5"/>
        <!-- Outer HORI Red/Black Rim -->
        <circle cx="50" cy="50" r="40" fill="none" stroke="#dc2626" stroke-width="10"/>
        <path d="M 18 32 A 40 40 0 0 0 18 68" fill="none" stroke="#1e293b" stroke-width="10.5" stroke-linecap="round"/>
        <path d="M 82 32 A 40 40 0 0 1 82 68" fill="none" stroke="#1e293b" stroke-width="10.5" stroke-linecap="round"/>
        <!-- Top Silver/White Center Marker -->
        <rect x="47" y="5" width="6" height="10" rx="1.5" fill="#f8fafc"/>
        <!-- Spokes (D-Pad left, Buttons right, Red bottom V) -->
        <path d="M 16 50 L 84 50 L 62 64 L 54 85 L 46 85 L 38 64 Z" fill="#1e293b" stroke="#475569" stroke-width="1.5"/>
        <!-- D-Pad on Left Spoke -->
        <path d="M 27 46 H 31 V 49 H 34 V 53 H 31 V 56 H 27 V 53 H 24 V 49 H 27 Z" fill="#94a3b8"/>
        <!-- Y/X/B/A Buttons on Right Spoke -->
        <circle cx="71" cy="46" r="2.2" fill="#38bdf8"/>
        <circle cx="71" cy="55" r="2.2" fill="#fbbf24"/>
        <circle cx="66.5" cy="50.5" r="2.2" fill="#4ade80"/>
        <circle cx="75.5" cy="50.5" r="2.2" fill="#f87171"/>
        <!-- Center Emblem -->
        <circle cx="50" cy="51" r="11" fill="#f8fafc" stroke="#dc2626" stroke-width="2.5"/>
        <text x="50" y="55" text-anchor="middle" font-size="11" font-weight="900" fill="#dc2626" font-family="sans-serif">M</text>
      </svg>
    `;

    this.wheelAngleLabel = document.createElement('span');
    this.wheelAngleLabel.id = 'hud-wheel-angle-label';
    this.wheelAngleLabel.style.cssText = `
      font-family: 'Chakra Petch', monospace; font-size: 11px; font-weight: 800;
      color: #38bdf8; background: rgba(56, 189, 248, 0.12);
      padding: 1px 7px; border-radius: 4px; border: 1px solid rgba(56, 189, 248, 0.35);
    `;
    this.wheelAngleLabel.innerText = '0°';
    wheelCol.appendChild(this.wheelSvgRotor);
    wheelCol.appendChild(this.wheelAngleLabel);

    // Right Detachable Pedal: GAS (ZR)
    const throttleCol = document.createElement('div');
    throttleCol.style.cssText = `display:flex;flex-direction:column;align-items:center;gap:3px;width:44px;`;
    throttleCol.innerHTML = `<span style="font-size:9px;font-weight:800;color:#4ade80;">⚡ GAS</span>`;
    const throttleTrack = document.createElement('div');
    throttleTrack.style.cssText = `
      width: 16px; height: 58px; background: #0f172a; border: 1px solid #475569;
      border-radius: 5px; overflow: hidden; display: flex; align-items: flex-end;
    `;
    this.wheelThrottleBar = document.createElement('div');
    this.wheelThrottleBar.id = 'hud-wheel-throttle-bar';
    this.wheelThrottleBar.style.cssText = `
      width: 100%; height: 0%;
      background: linear-gradient(180deg, #34d399 0%, #059669 100%);
      transition: height 0.04s linear;
    `;
    throttleTrack.appendChild(this.wheelThrottleBar);
    this.wheelThrottlePct = document.createElement('span');
    this.wheelThrottlePct.id = 'hud-wheel-throttle-pct';
    this.wheelThrottlePct.style.cssText = `font-family:monospace;font-size:10px;font-weight:700;color:#86efac;`;
    this.wheelThrottlePct.innerText = '0%';
    throttleCol.appendChild(throttleTrack);
    throttleCol.appendChild(this.wheelThrottlePct);

    gaugesRow.appendChild(brakeCol);
    gaugesRow.appendChild(wheelCol);
    gaugesRow.appendChild(throttleCol);
    this.wheelTelemetryCard.appendChild(gaugesRow);

    // Compact StVO Wheel Mapping Cheat Sheet
    this.wheelPaddleHint = document.createElement('div');
    this.wheelPaddleHint.id = 'hud-wheel-paddle-hint';
    this.wheelPaddleHint.style.cssText = `
      display: grid; grid-template-columns: 1fr 1fr; gap: 3px 6px;
      font-size: 9.5px; color: #cbd5e1; font-weight: 600;
      background: rgba(30, 41, 59, 0.65); padding: 5px 7px; border-radius: 7px;
    `;
    this.wheelPaddleHint.innerHTML = `
      <span>🪞 <b>D-Pad:</b> Spiegel</span>
      <span>👀 <b>Y / A:</b> Schulter</span>
      <span>◀▶ <b>Paddel:</b> Blinker</span>
      <span>⚙️ <b>X / B:</b> Gang D/R</span>
    `;
    this.wheelTelemetryCard.appendChild(this.wheelPaddleHint);

    this.container.appendChild(this.wheelTelemetryCard);
  }

  public toggleWheelTelemetryCard(forceOpen?: boolean) {
    if (!this.wheelTelemetryCard) return;
    const isHidden = this.wheelTelemetryCard.getAttribute('data-visible') === 'false' || this.wheelTelemetryCard.style.display === 'none';
    const shouldOpen = forceOpen !== undefined ? forceOpen : isHidden;
    this.wheelTelemetryCard.style.display = shouldOpen ? 'flex' : 'none';
    this.wheelTelemetryCard.setAttribute('data-visible', shouldOpen ? 'true' : 'false');
    if (this.btnWheelTelemetry) {
      this.btnWheelTelemetry.style.background = shouldOpen ? 'rgba(220, 38, 38, 0.55)' : 'rgba(153, 27, 27, 0.35)';
      this.btnWheelTelemetry.style.boxShadow = shouldOpen ? '0 0 12px rgba(239, 68, 68, 0.6)' : 'none';
    }
  }

  public updateWheelTelemetry(wt: {
    connected: boolean;
    deviceName: string;
    steeringAngleDeg: number;
    throttlePct: number;
    brakePct: number;
    rotationDegrees: number;
    sensitivityLevel: number;
    paddleMode: 'blinkers' | 'gears';
  }) {
    if (!this.wheelTelemetryCard) return;

    // Automatically open the telemetry card the first time a physical wheel is detected
    if (wt.connected && !this.wheelAutoOpened) {
      this.wheelAutoOpened = true;
      this.toggleWheelTelemetryCard(true);
    }

    if (this.wheelStatusBadge) {
      this.wheelStatusBadge.innerText = wt.connected
        ? `🟢 ${wt.rotationDegrees}° • S${wt.sensitivityLevel}`
        : `⚪ ${wt.rotationDegrees}° • S${wt.sensitivityLevel}`;
      this.wheelStatusBadge.style.borderColor = wt.connected ? '#10b981' : '#64748b';
      this.wheelStatusBadge.style.color = wt.connected ? '#34d399' : '#cbd5e1';
    }

    if (this.wheelSvgRotor) {
      this.wheelSvgRotor.style.transform = `rotate(${wt.steeringAngleDeg}deg)`;
    }
    if (this.wheelAngleLabel) {
      const sign = wt.steeringAngleDeg > 0 ? '+' : '';
      this.wheelAngleLabel.innerText = `${sign}${wt.steeringAngleDeg}°`;
    }
    if (this.wheelThrottleBar && this.wheelThrottlePct) {
      const tPct = Math.max(0, Math.min(100, wt.throttlePct));
      this.wheelThrottleBar.style.height = `${tPct}%`;
      this.wheelThrottlePct.innerText = `${tPct}%`;
    }
    if (this.wheelBrakeBar && this.wheelBrakePct) {
      const bPct = Math.max(0, Math.min(100, wt.brakePct));
      this.wheelBrakeBar.style.height = `${bPct}%`;
      this.wheelBrakePct.innerText = `${bPct}%`;
    }
    if (this.wheelPaddleHint) {
      const paddleText = wt.paddleMode === 'gears' ? '⚙️ <b>Paddel:</b> Gang -/+' : '◀▶ <b>Paddel:</b> Blinker';
      this.wheelPaddleHint.innerHTML = `
        <span>🪞 <b>D-Pad:</b> Spiegel</span>
        <span>👀 <b>Y / A:</b> Schulter</span>
        <span>${paddleText}</span>
        <span>⚙️ <b>X / B:</b> Gang D/R</span>
      `;
    }
  }

  public update(
    telemetry: VehicleTelemetry,
    speedLimit: number,
    blinkerState: string,
    deltaTime: number = 0.016,
    activeTrafficLightPhase?: string | null
  ) {
    let timeString = '12:00';
    if (typeof window !== 'undefined') {
      const now = new Date();
      timeString = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    }

    // Render Canvas Virtual Cockpit Dashboard (Gauges, Bordcomputer, Needles)
    this.dashboardCluster.render(telemetry, speedLimit, blinkerState, timeString, deltaTime);

    this.speedLimitBadge.innerText = `${speedLimit}`;

    // Live Mini Traffic Light Badge Update
    if (this.trafficLightBadge) {
      if (activeTrafficLightPhase) {
        this.trafficLightBadge.style.display = 'flex';
        const isRed = activeTrafficLightPhase === 'RED' || activeTrafficLightPhase === 'RED_YELLOW';
        const isYellow = activeTrafficLightPhase === 'RED_YELLOW' || activeTrafficLightPhase === 'YELLOW';
        const isGreen = activeTrafficLightPhase === 'GREEN';

        const rEl = this.trafficLightBadge.querySelector('#tl-indicator-r') as HTMLElement;
        const yEl = this.trafficLightBadge.querySelector('#tl-indicator-y') as HTMLElement;
        const gEl = this.trafficLightBadge.querySelector('#tl-indicator-g') as HTMLElement;

        if (rEl) {
          rEl.style.background = isRed ? '#ef4444' : '#450a0a';
          rEl.style.boxShadow = isRed ? '0 0 10px #ef4444' : 'none';
        }
        if (yEl) {
          yEl.style.background = isYellow ? '#fbbf24' : '#422006';
          yEl.style.boxShadow = isYellow ? '0 0 10px #fbbf24' : 'none';
        }
        if (gEl) {
          gEl.style.background = isGreen ? '#22c55e' : '#052e16';
          gEl.style.boxShadow = isGreen ? '0 0 10px #22c55e' : 'none';
        }
      } else {
        this.trafficLightBadge.style.display = 'none';
      }
    }

    // ── Live Acceleration & Speed Calculation ────────────────────────────────
    const speedMs = Math.abs(telemetry.speedKmh) / 3.6;
    const accelMs2 = deltaTime > 0 ? (speedMs - this.lastSpeedMs) / deltaTime : 0;
    this.lastSpeedMs = speedMs;
    this.smoothedAccelMs2 += (accelMs2 - this.smoothedAccelMs2) * Math.min(1.0, deltaTime * 8);

    const speedRounded = Math.round(Math.abs(telemetry.speedKmh));
    if (this.speedNumEl) {
      this.speedNumEl.innerText = `${speedRounded}`;
      if (speedRounded > speedLimit + 8) {
        this.speedNumEl.style.color = '#ef4444';
        this.speedNumEl.style.textShadow = '0 0 12px rgba(239, 68, 68, 0.8)';
      } else if (speedRounded > speedLimit + 3) {
        this.speedNumEl.style.color = '#f59e0b';
        this.speedNumEl.style.textShadow = '0 0 12px rgba(245, 158, 11, 0.8)';
      } else {
        this.speedNumEl.style.color = '#38bdf8';
        this.speedNumEl.style.textShadow = '0 0 10px rgba(56, 189, 248, 0.6)';
      }
    }

    if (this.gearBadge) {
      const g = telemetry.gear || 'P';
      this.gearBadge.innerText = g;
      if (g === 'R') {
        this.gearBadge.style.color = '#ef4444';
        this.gearBadge.style.borderColor = '#ef4444';
        this.gearBadge.style.boxShadow = '0 0 10px rgba(239, 68, 68, 0.35)';
      } else if (g.startsWith('D') || g === '1' || g === '2' || g === '3' || g === '4' || g === '5') {
        this.gearBadge.style.color = '#4ade80';
        this.gearBadge.style.borderColor = '#22c55e';
        this.gearBadge.style.boxShadow = '0 0 10px rgba(34, 197, 94, 0.35)';
      } else if (g === 'P') {
        this.gearBadge.style.color = '#fbbf24';
        this.gearBadge.style.borderColor = '#f59e0b';
        this.gearBadge.style.boxShadow = '0 0 10px rgba(245, 158, 11, 0.25)';
      } else {
        this.gearBadge.style.color = '#94a3b8';
        this.gearBadge.style.borderColor = '#64748b';
        this.gearBadge.style.boxShadow = 'none';
      }
    }

    if (this.accelValEl) {
      const sign = this.smoothedAccelMs2 >= 0 ? '+' : '';
      this.accelValEl.innerText = `${sign}${this.smoothedAccelMs2.toFixed(1)} m/s²`;
      if (this.smoothedAccelMs2 > 0.3) {
        this.accelValEl.style.color = '#4ade80';
        if (this.accelBarEl) {
          const pct = Math.min(100, (this.smoothedAccelMs2 / 4.0) * 100);
          this.accelBarEl.style.width = `${pct}%`;
          this.accelBarEl.style.background = '#22c55e';
        }
      } else if (this.smoothedAccelMs2 < -0.4) {
        this.accelValEl.style.color = '#ef4444';
        if (this.accelBarEl) {
          const pct = Math.min(100, (Math.abs(this.smoothedAccelMs2) / 6.0) * 100);
          this.accelBarEl.style.width = `${pct}%`;
          this.accelBarEl.style.background = '#ef4444';
        }
      } else {
        this.accelValEl.style.color = '#94a3b8';
        if (this.accelBarEl) {
          this.accelBarEl.style.width = '0%';
        }
      }
    }

    if (this.gearButton) {
      this.gearButton.innerHTML = `⚙️ Gang: ${telemetry.gear || 'P'} [R/G]`;
    }
    if (this.speedBadgeBottom) {
      this.speedBadgeBottom.innerHTML = `⚡ ${speedRounded} km/h`;
    }

    // Blinkers on top telemetry bar & quick control bar
    const isBlinkOn = blinkerState !== 'off' && Math.floor(Date.now() / 380) % 2 === 0;
    const isLeftEngaged = blinkerState === 'left' || blinkerState === 'hazard';
    const isRightEngaged = blinkerState === 'right' || blinkerState === 'hazard';
    const leftActive = isLeftEngaged && isBlinkOn;
    const rightActive = isRightEngaged && isBlinkOn;

    if (this.hudBlinkerL) {
      this.hudBlinkerL.style.color = leftActive ? '#22c55e' : (isLeftEngaged ? '#15803d' : '#64748b');
      this.hudBlinkerL.style.opacity = leftActive ? '1.0' : (isLeftEngaged ? '0.6' : '0.35');
      this.hudBlinkerL.style.textShadow = leftActive ? '0 0 14px #22c55e' : 'none';
      this.hudBlinkerL.style.transform = leftActive ? 'scale(1.25)' : 'scale(1.0)';
    }
    if (this.hudBlinkerR) {
      this.hudBlinkerR.style.color = rightActive ? '#22c55e' : (isRightEngaged ? '#15803d' : '#64748b');
      this.hudBlinkerR.style.opacity = rightActive ? '1.0' : (isRightEngaged ? '0.6' : '0.35');
      this.hudBlinkerR.style.textShadow = rightActive ? '0 0 14px #22c55e' : 'none';
      this.hudBlinkerR.style.transform = rightActive ? 'scale(1.25)' : 'scale(1.0)';
    }

    if (this.blinkerL) {
      if (isLeftEngaged) {
        this.blinkerL.style.opacity = '1.0';
        this.blinkerL.style.color = leftActive ? '#ffffff' : '#86efac';
        this.blinkerL.style.background = leftActive ? 'rgba(34, 197, 94, 0.55)' : 'rgba(22, 101, 52, 0.45)';
        this.blinkerL.style.borderColor = '#22c55e';
        this.blinkerL.style.boxShadow = leftActive ? '0 0 16px rgba(34, 197, 94, 0.95)' : '0 0 8px rgba(34, 197, 94, 0.35)';
      } else {
        this.blinkerL.style.opacity = '0.7';
        this.blinkerL.style.color = '#94a3b8';
        this.blinkerL.style.background = 'rgba(30, 41, 59, 0.6)';
        this.blinkerL.style.borderColor = '#475569';
        this.blinkerL.style.boxShadow = 'none';
      }
    }
    if (this.blinkerR) {
      if (isRightEngaged) {
        this.blinkerR.style.opacity = '1.0';
        this.blinkerR.style.color = rightActive ? '#ffffff' : '#86efac';
        this.blinkerR.style.background = rightActive ? 'rgba(34, 197, 94, 0.55)' : 'rgba(22, 101, 52, 0.45)';
        this.blinkerR.style.borderColor = '#22c55e';
        this.blinkerR.style.boxShadow = rightActive ? '0 0 16px rgba(34, 197, 94, 0.95)' : '0 0 8px rgba(34, 197, 94, 0.35)';
      } else {
        this.blinkerR.style.opacity = '0.7';
        this.blinkerR.style.color = '#94a3b8';
        this.blinkerR.style.background = 'rgba(30, 41, 59, 0.6)';
        this.blinkerR.style.borderColor = '#475569';
        this.blinkerR.style.boxShadow = 'none';
      }
    }

    // Stall Warning
    this.stallAlert.style.display = telemetry.isStalled ? 'block' : 'none';

    // Seatbelt Lamp Update
    if (telemetry.seatbeltFastened) {
      this.seatbeltLamp.style.background = 'rgba(22, 101, 52, 0.65)';
      this.seatbeltLamp.style.borderColor = '#22c55e';
      this.seatbeltLamp.style.color = '#4ade80';
      this.seatbeltLamp.innerHTML = '🔒 Gurt: ✅ [B]';
    } else {
      this.seatbeltLamp.style.background = 'rgba(220, 38, 38, 0.45)';
      this.seatbeltLamp.style.borderColor = '#ef4444';
      this.seatbeltLamp.style.color = '#ef4444';
      this.seatbeltLamp.innerHTML = '🔒 Gurt [B]';
    }

    // Handbrake Lamp Update (Green when released and ready to roll)
    if (telemetry.handbrake) {
      this.handbrakeLamp.style.background = 'rgba(220, 38, 38, 0.45)';
      this.handbrakeLamp.style.borderColor = '#ef4444';
      this.handbrakeLamp.style.color = '#ef4444';
      this.handbrakeLamp.innerHTML = '🅿️ Handbremse [Space]';
    } else {
      this.handbrakeLamp.style.background = 'rgba(22, 101, 52, 0.65)';
      this.handbrakeLamp.style.borderColor = '#22c55e';
      this.handbrakeLamp.style.color = '#4ade80';
      this.handbrakeLamp.innerHTML = '🅿️ Gelöst [Space]';
    }

    // Engine Lamp Update (Green when engine is smoothly running)
    if (telemetry.engineRunning && !telemetry.isStalled) {
      this.engineLamp.style.background = 'rgba(22, 101, 52, 0.65)';
      this.engineLamp.style.borderColor = '#22c55e';
      this.engineLamp.style.color = '#4ade80';
      this.engineLamp.innerHTML = '🔑 Motor AN [I]';
    } else {
      this.engineLamp.style.background = 'rgba(234, 179, 8, 0.45)';
      this.engineLamp.style.borderColor = '#eab308';
      this.engineLamp.style.color = '#eab308';
      this.engineLamp.innerHTML = '🔑 Motor [I]';
    }

    // Pre-Drive Checklist Progress
    this.updateChecklistProgress(telemetry);
  }

  public onToggleCamera(cb: () => void) {
    this.onToggleCameraCallback = cb;
  }

  public setCameraMode(mode: string) {
    this.currentCameraMode = mode;
    if (this.btnCameraToggle) {
      if (mode === 'cockpit' || mode === 'cockpit-driver') {
        this.btnCameraToggle.innerHTML = '🎥 Cockpit [V]';
      } else if (mode === 'cockpit-dash') {
        this.btnCameraToggle.innerHTML = '🎥 Haube [V]';
      } else if (mode === 'cockpit-hood') {
        this.btnCameraToggle.innerHTML = '🎥 Stoßstange [V]';
      } else if (mode === 'chase') {
        this.btnCameraToggle.innerHTML = '🎥 Verfolger [V]';
      } else if (mode === 'topdown') {
        this.btnCameraToggle.innerHTML = '🎥 Draufsicht [V]';
      }
    }

    // In 'cockpit-driver' (1st person), the 3D interior features live analog gauges & steering wheel.
    // In 'cockpit-dash' (Fahrsimulator mode), 'cockpit-hood', 'chase', and 'topdown',
    // render the 2D classic instrument cluster at the bottom center (as seen in Fahrsimulator 2013).
    const isCockpitMode = (mode === 'cockpit' || mode === 'cockpit-driver');
    if (this.dashboardCluster && this.dashboardCluster.canvas) {
      this.dashboardCluster.canvas.style.display = isCockpitMode ? 'none' : 'block';
    }

    if (this.container) {
      if (isCockpitMode) {
        this.container.classList.add('cockpit-clean-hud');
      } else {
        this.container.classList.remove('cockpit-clean-hud');
      }
    }
  }

  public setMouseSteeringActive(active: boolean) {
    if (!this.btnMouseSteer) return;
    if (active) {
      this.btnMouseSteer.style.background = 'rgba(2, 132, 199, 0.45)';
      this.btnMouseSteer.style.borderColor = '#38bdf8';
      this.btnMouseSteer.style.color = '#38bdf8';
      this.btnMouseSteer.style.boxShadow = '0 0 14px rgba(56, 189, 248, 0.5)';
      this.btnMouseSteer.innerHTML = '🖱️ Maus AN [M]';
    } else {
      this.btnMouseSteer.style.background = 'rgba(30, 41, 59, 0.6)';
      this.btnMouseSteer.style.borderColor = '#475569';
      this.btnMouseSteer.style.color = '#94a3b8';
      this.btnMouseSteer.style.boxShadow = 'none';
      this.btnMouseSteer.innerHTML = '🖱️ Maus [M]';
    }
  }


  private updateChecklistProgress(telemetry: VehicleTelemetry) {
    const isGearReady = telemetry.gear !== 'P' && telemetry.gear !== 'N' && telemetry.gear !== '0';
    
    // 1. Seat & Mirrors
    const item1 = this.checklistItems.get('seatMirrors');
    if (item1) {
      if (this.seatMirrorsAdjusted) {
        item1.row.style.background = 'rgba(22, 101, 52, 0.35)';
        item1.row.style.borderColor = '#22c55e';
        item1.row.style.boxShadow = '0 0 12px rgba(34, 197, 94, 0.25)';
        item1.statusEl.innerText = '✅ Eingestellt';
        item1.statusEl.style.color = '#4ade80';
        if (item1.btnAction) {
          item1.btnAction.innerText = 'Ändern 🪑';
          item1.btnAction.style.background = '#15803d';
          item1.btnAction.style.borderColor = '#22c55e';
        }
      } else {
        item1.row.style.background = 'rgba(30, 41, 59, 0.75)';
        item1.row.style.borderColor = 'rgba(245, 158, 11, 0.4)';
        item1.row.style.boxShadow = 'none';
        item1.statusEl.innerText = '⭕ Offen';
        item1.statusEl.style.color = '#f59e0b';
        if (item1.btnAction) {
          item1.btnAction.innerText = 'Anpassen 🪞';
          item1.btnAction.style.background = '#4f46e5';
          item1.btnAction.style.borderColor = '#6366f1';
        }
      }
    }

    // 2. Seatbelt
    const item2 = this.checklistItems.get('seatbelt');
    if (item2) {
      if (telemetry.seatbeltFastened) {
        item2.row.style.background = 'rgba(22, 101, 52, 0.35)';
        item2.row.style.borderColor = '#22c55e';
        item2.row.style.boxShadow = '0 0 12px rgba(34, 197, 94, 0.25)';
        item2.statusEl.innerText = '✅ Angeschnallt';
        item2.statusEl.style.color = '#4ade80';
        if (item2.btnAction) {
          item2.btnAction.innerText = 'Abschnallen [B]';
          item2.btnAction.style.background = '#15803d';
          item2.btnAction.style.borderColor = '#22c55e';
        }
      } else {
        item2.row.style.background = 'rgba(30, 41, 59, 0.75)';
        item2.row.style.borderColor = 'rgba(239, 68, 68, 0.4)';
        item2.row.style.boxShadow = 'none';
        item2.statusEl.innerText = '⭕ Unangeschnallt';
        item2.statusEl.style.color = '#ef4444';
        if (item2.btnAction) {
          item2.btnAction.innerText = 'Anschnallen [B]';
          item2.btnAction.style.background = '#2563eb';
          item2.btnAction.style.borderColor = '#3b82f6';
        }
      }
    }

    // 3. Engine
    const item3 = this.checklistItems.get('engine');
    if (item3) {
      if (telemetry.engineRunning && !telemetry.isStalled) {
        item3.row.style.background = 'rgba(22, 101, 52, 0.35)';
        item3.row.style.borderColor = '#22c55e';
        item3.row.style.boxShadow = '0 0 12px rgba(34, 197, 94, 0.25)';
        item3.statusEl.innerText = '✅ Motor läuft';
        item3.statusEl.style.color = '#4ade80';
        if (item3.btnAction) {
          item3.btnAction.innerText = 'Stoppen [I]';
          item3.btnAction.style.background = '#15803d';
          item3.btnAction.style.borderColor = '#22c55e';
        }
      } else {
        item3.row.style.background = 'rgba(30, 41, 59, 0.75)';
        item3.row.style.borderColor = 'rgba(239, 68, 68, 0.4)';
        item3.row.style.boxShadow = 'none';
        item3.statusEl.innerText = '⭕ Ausgeschaltet';
        item3.statusEl.style.color = '#ef4444';
        if (item3.btnAction) {
          item3.btnAction.innerText = 'Starten [I]';
          item3.btnAction.style.background = '#2563eb';
          item3.btnAction.style.borderColor = '#3b82f6';
        }
      }
    }

    // 4. Handbrake
    const item4 = this.checklistItems.get('handbrake');
    if (item4) {
      if (!telemetry.handbrake) {
        item4.row.style.background = 'rgba(22, 101, 52, 0.35)';
        item4.row.style.borderColor = '#22c55e';
        item4.row.style.boxShadow = '0 0 12px rgba(34, 197, 94, 0.25)';
        item4.statusEl.innerText = '✅ Gelöst';
        item4.statusEl.style.color = '#4ade80';
        if (item4.btnAction) {
          item4.btnAction.innerText = 'Anziehen [Space]';
          item4.btnAction.style.background = '#15803d';
          item4.btnAction.style.borderColor = '#22c55e';
        }
      } else {
        item4.row.style.background = 'rgba(30, 41, 59, 0.75)';
        item4.row.style.borderColor = 'rgba(245, 158, 11, 0.4)';
        item4.row.style.boxShadow = 'none';
        item4.statusEl.innerText = '⭕ Angezogen';
        item4.statusEl.style.color = '#f59e0b';
        if (item4.btnAction) {
          item4.btnAction.innerText = 'Lösen [Space]';
          item4.btnAction.style.background = '#2563eb';
          item4.btnAction.style.borderColor = '#3b82f6';
        }
      }
    }

    // 5. Gear
    const item5 = this.checklistItems.get('gear');
    if (item5) {
      if (isGearReady) {
        item5.row.style.background = 'rgba(22, 101, 52, 0.35)';
        item5.row.style.borderColor = '#22c55e';
        item5.row.style.boxShadow = '0 0 12px rgba(34, 197, 94, 0.25)';
        item5.statusEl.innerText = `✅ Gang ${telemetry.gear}`;
        item5.statusEl.style.color = '#4ade80';
        if (item5.btnAction) {
          item5.btnAction.innerText = 'P/N einlegen [G]';
          item5.btnAction.style.background = '#15803d';
          item5.btnAction.style.borderColor = '#22c55e';
        }
      } else {
        item5.row.style.background = 'rgba(30, 41, 59, 0.75)';
        item5.row.style.borderColor = 'rgba(245, 158, 11, 0.4)';
        item5.row.style.boxShadow = 'none';
        item5.statusEl.innerText = `⭕ In Park/Neutral (${telemetry.gear})`;
        item5.statusEl.style.color = '#f59e0b';
        if (item5.btnAction) {
          item5.btnAction.innerText = 'D einlegen [R]';
          item5.btnAction.style.background = '#2563eb';
          item5.btnAction.style.borderColor = '#3b82f6';
        }
      }
    }

    // Overall readiness
    const allDone = this.seatMirrorsAdjusted && telemetry.seatbeltFastened && telemetry.engineRunning && !telemetry.handbrake && isGearReady;
    if (allDone && !this.preDriveCompleted) {
      this.preDriveCompleted = true;
      this.checklistCard.style.borderColor = '#22c55e';
      this.checklistCard.style.boxShadow = '0 0 35px rgba(34, 197, 94, 0.7)';
      // Smooth fade out and hide completely after 1 second so driver has full road view
      if (this.checklistAutoCloseTimer) {
        clearTimeout(this.checklistAutoCloseTimer);
      }
      this.checklistAutoCloseTimer = setTimeout(() => {
        if (this.preDriveCompleted) {
          this.toggleChecklistCard(false);
        }
      }, 1000);
    }
  }

  public toggleChecklistCard(forceOpen?: boolean) {
    if (!this.checklistCard) return;

    if (this.checklistAutoCloseTimer) {
      clearTimeout(this.checklistAutoCloseTimer);
      this.checklistAutoCloseTimer = null;
    }

    const isHidden = this.checklistCard.getAttribute('data-visible') === 'false'
      || this.checklistCard.style.display === 'none';

    const shouldOpen = forceOpen !== undefined ? forceOpen : isHidden;
    const isMobile = typeof window !== 'undefined' && (window.innerWidth <= 900 || window.innerHeight <= 550);

    if (shouldOpen) {
      if (this.checklistBackdrop) {
        this.checklistBackdrop.style.display = 'block';
        this.checklistBackdrop.style.pointerEvents = 'auto';
        if (typeof requestAnimationFrame !== 'undefined') {
          requestAnimationFrame(() => {
            if (this.checklistBackdrop) {
              this.checklistBackdrop.style.opacity = '1';
            }
          });
        } else {
          this.checklistBackdrop.style.opacity = '1';
        }
      }

      this.checklistCard.style.display = 'flex';
      this.checklistCard.style.flexDirection = 'column';
      this.checklistCard.style.opacity = '0';
      this.checklistCard.style.transform = isMobile
        ? 'translate(-50%, -50%) scale(0.97)'
        : 'translateY(-12px) scale(0.97)';
      this.checklistCard.setAttribute('data-visible', 'true');
      this.checklistCard.setAttribute('data-collapsed', 'false');

      const itemsList = document.getElementById('hud-checklist-items');
      if (itemsList) itemsList.style.display = 'flex';

      const btnMinimize = this.checklistCard.querySelector('#hud-btn-checklist-minimize') as HTMLElement;
      if (btnMinimize) btnMinimize.innerText = '−';

      if (this.btnChecklistToggle) {
        this.btnChecklistToggle.style.background = 'rgba(37, 99, 235, 0.9)';
        this.btnChecklistToggle.style.borderColor = '#60a5fa';
        this.btnChecklistToggle.style.boxShadow = '0 0 14px rgba(59, 130, 246, 0.5)';
      }

      const applyOpenStyles = () => {
        this.checklistCard.style.transition = 'opacity 0.25s, transform 0.25s';
        this.checklistCard.style.opacity = '1';
        this.checklistCard.style.transform = isMobile
          ? 'translate(-50%, -50%) scale(1)'
          : 'translateY(0) scale(1)';
        this.checklistCard.style.pointerEvents = 'auto';
      };
      if (typeof requestAnimationFrame !== 'undefined') {
        requestAnimationFrame(applyOpenStyles);
      } else {
        applyOpenStyles();
      }
    } else {
      if (this.checklistBackdrop) {
        this.checklistBackdrop.style.opacity = '0';
        this.checklistBackdrop.style.pointerEvents = 'none';
        setTimeout(() => {
          if (this.checklistBackdrop && this.checklistCard.getAttribute('data-visible') === 'false') {
            this.checklistBackdrop.style.display = 'none';
          }
        }, 200);
      }

      this.checklistCard.style.transition = 'opacity 0.2s, transform 0.2s';
      this.checklistCard.style.opacity = '0';
      this.checklistCard.style.transform = isMobile
        ? 'translate(-50%, -50%) scale(0.97)'
        : 'translateY(-12px) scale(0.97)';
      this.checklistCard.style.pointerEvents = 'none';

      if (this.btnChecklistToggle) {
        this.btnChecklistToggle.style.background = 'rgba(30, 41, 59, 0.85)';
        this.btnChecklistToggle.style.borderColor = '#475569';
        this.btnChecklistToggle.style.boxShadow = 'none';
      }

      setTimeout(() => {
        this.checklistCard.style.display = 'none';
        this.checklistCard.setAttribute('data-visible', 'false');
      }, 200);
    }
  }

  public showInfractionBanner(title: string, durationMs: number = 4000) {
    if (!this.infractionBanner) return;
    this.infractionBanner.innerHTML = `<span style="font-size:15px;margin-right:6px;">🚨</span><span>${title}</span>`;
    this.infractionBanner.style.display = 'block';
    setTimeout(() => {
      if (this.infractionBanner) {
        this.infractionBanner.style.display = 'none';
      }
    }, durationMs);
  }

  public onOpenSettings(cb: () => void) {
    this.onOpenSettingsCallback = cb;
  }

  public onOpenCockpitAdjuster(cb: () => void) {
    this.onOpenCockpitAdjusterCallback = cb;
  }

  public onToggleTrajectory(cb: () => void) {
    this.onToggleTrajectoryCallback = cb;
  }

  public onReturnToMenu(cb: () => void) {
    this.onReturnToMenuCallback = cb;
  }

  public onToggleSeatbelt(cb: () => void) {
    this.onToggleSeatbeltCallback = cb;
  }

  public onFastenSeatbelt(cb: () => void) {
    this.onFastenSeatbeltCallback = cb;
  }

  public onToggleEngine(cb: () => void) {
    this.onToggleEngineCallback = cb;
  }

  public onStartEngine(cb: () => void) {
    this.onStartEngineCallback = cb;
  }

  public onToggleHandbrake(cb: () => void) {
    this.onToggleHandbrakeCallback = cb;
  }

  public onReleaseHandbrake(cb: () => void) {
    this.onReleaseHandbrakeCallback = cb;
  }

  public onSelectDriveGear(cb: () => void) {
    this.onSelectDriveGearCallback = cb;
  }

  public onToggleBlinkerLeft(cb: () => void) {
    this.onBlinkerLeftCallback = cb;
  }

  public onToggleBlinkerRight(cb: () => void) {
    this.onBlinkerRightCallback = cb;
  }

  public showShoulderCheck(active: 'left' | 'right' | null, progress: number) {
    if (!this.shoulderVignette) return;

    if (active === null || progress < 0.05) {
      this.shoulderVignette.style.opacity = '0';
      this.shoulderLabel.style.opacity = '0';
      return;
    }

    const alpha = Math.min(1.0, progress * 1.4).toFixed(2);
    this.shoulderVignette.style.opacity = alpha;
    this.shoulderLabel.style.opacity = alpha;

    if (active === 'left') {
      this.shoulderVignette.style.background =
        'linear-gradient(to right, rgba(37,99,235,0.45) 0%, rgba(37,99,235,0.12) 35%, transparent 65%)';
      this.shoulderLabel.style.left = '16px';
      this.shoulderLabel.style.right = '';
      this.shoulderLabel.innerText = '👈 Schulterblick links';
    } else {
      this.shoulderVignette.style.background =
        'linear-gradient(to left, rgba(37,99,235,0.45) 0%, rgba(37,99,235,0.12) 35%, transparent 65%)';
      this.shoulderLabel.style.right = '16px';
      this.shoulderLabel.style.left = '';
      this.shoulderLabel.innerText = 'Schulterblick rechts 👉';
    }
  }

  public logInfraction(infraction: StVOInfraction, totalScore: number) {
    const rounded = Math.max(0, Math.round(totalScore));
    this.scoreValue.innerText = `${rounded}`;
    if (totalScore >= 85) {
      this.scoreValue.style.color = '#22c55e';
    } else if (totalScore >= 65) {
      this.scoreValue.style.color = '#f59e0b';
    } else {
      this.scoreValue.style.color = '#ef4444';
    }

    const scorePillVal = document.getElementById('hud-score-pill-val');
    if (scorePillVal) {
      scorePillVal.innerText = `${rounded}`;
      scorePillVal.style.color = totalScore >= 85 ? '#4ade80' : totalScore >= 65 ? '#f59e0b' : '#ef4444';
    }

    const placeholder = document.getElementById('hud-no-infraction-placeholder');
    if (placeholder) {
      if (typeof placeholder.remove === 'function') {
        placeholder.remove();
      } else if (placeholder.parentElement) {
        placeholder.parentElement.removeChild(placeholder);
      }
    }

    const sevColor = infraction.severity === 'FATAL' ? '#ef4444'
                   : infraction.severity === 'MAJOR' ? '#f59e0b'
                   : '#64748b';
    const sevLabel = infraction.severity === 'FATAL' ? 'SCHWER'
                   : infraction.severity === 'MAJOR' ? 'MITTEL'
                   : 'LEICHT';

    const key = infraction.code + '_' + infraction.title;
    const existing = this.infractionRowMap.get(key);
    if (existing) {
      existing.count++;
      existing.countEl.innerText = `×${existing.count}`;
      existing.row.classList.remove('hud-infraction-new');
      void existing.row.offsetWidth;
      existing.row.classList.add('hud-infraction-new');
      return;
    }

    const row = document.createElement('div');
    row.style.cssText = `
      display: flex; flex-direction: column; gap: 2px;
      padding: 5px 6px; border-radius: 7px;
      border-left: 3px solid ${sevColor};
      background: rgba(255,255,255,0.04);
    `;
    row.classList.add('hud-infraction-new');

    const topLine = document.createElement('div');
    topLine.style.cssText = `display: flex; align-items: center; gap: 5px; justify-content: space-between;`;

    const codeBadge = document.createElement('span');
    codeBadge.style.cssText = `font-family: 'Chakra Petch', sans-serif; font-size: 9px; font-weight: 700; color: ${sevColor}; letter-spacing: 0.06em;`;
    codeBadge.innerText = infraction.code;

    const sevBadge = document.createElement('span');
    sevBadge.style.cssText = `font-size: 9px; font-weight: 700; color: ${sevColor}; background: rgba(255,255,255,0.06); padding: 1px 5px; border-radius: 4px;`;
    sevBadge.innerText = sevLabel;

    const countEl = document.createElement('span');
    countEl.style.cssText = `font-family: 'Chakra Petch', sans-serif; font-size: 11px; font-weight: 800; color: #cbd5e1; margin-left: auto;`;
    countEl.innerText = '×1';

    topLine.appendChild(codeBadge);
    topLine.appendChild(sevBadge);
    topLine.appendChild(countEl);

    const titleEl = document.createElement('div');
    titleEl.style.cssText = `font-size: 10px; color: #e2e8f0; font-weight: 600; line-height: 1.3; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;
    titleEl.innerText = infraction.title;

    row.appendChild(topLine);
    row.appendChild(titleEl);
    this.infractionList.appendChild(row);

    this.infractionList.scrollTop = this.infractionList.scrollHeight;
    this.infractionRowMap.set(key, { row, countEl, count: 1 });
  }

  public showActiveViolation(message: string | null) {
    if (!this.activeViolationStrip) return;
    if (message) {
      this.activeViolationStrip.style.display = 'block';
      this.activeViolationStrip.innerHTML = `<span style="font-size:15px;margin-right:6px;">⚠️</span><span>${message}</span>`;
    } else {
      this.activeViolationStrip.style.display = 'none';
    }
  }

  public showNavigationInstruction(instruction: {
    icon: string;
    title?: string;
    text: string;
    distanceMeters?: number;
    subText?: string;
    onReplay?: () => void;
  } | null) {
    if (!this.navigationBanner) return;

    if (!instruction) {
      this.navigationBanner.style.display = 'none';
      return;
    }

    this.navigationBanner.style.display = 'flex';
    this.navIconEl.innerHTML = instruction.icon || '🧭';
    this.navTitleEl.innerText = instruction.title ? `🧭 ${instruction.title}` : '🧭 FAHRPRÜFER MÜLLER • FAHRTANWEISUNG';
    this.navTextEl.innerText = instruction.text;

    let distStr = '';
    if (instruction.distanceMeters !== undefined && instruction.distanceMeters > 0) {
      if (instruction.distanceMeters <= 10) {
        distStr = `⚡ In ${instruction.distanceMeters} m – JETZT ABBIEGEN!`;
        this.navSubTextEl.style.color = '#fbbf24';
        this.navSubTextEl.style.fontWeight = '800';
      } else {
        distStr = `Distanz: ca. ${instruction.distanceMeters} m`;
        this.navSubTextEl.style.color = '#94a3b8';
        this.navSubTextEl.style.fontWeight = '600';
      }
    } else if (instruction.subText) {
      distStr = instruction.subText;
      this.navSubTextEl.style.color = '#94a3b8';
      this.navSubTextEl.style.fontWeight = '600';
    } else {
      distStr = 'Dem Straßenverlauf folgen';
      this.navSubTextEl.style.color = '#94a3b8';
      this.navSubTextEl.style.fontWeight = '600';
    }
    this.navSubTextEl.innerText = distStr;

    this.onNavReplayCallback = instruction.onReplay || null;
  }

  public resetScorePanel() {
    this.infractionRowMap.clear();
    this.infractionList.innerHTML = '';
    const placeholder = document.createElement('div');
    placeholder.id = 'hud-no-infraction-placeholder';
    placeholder.style.cssText = `font-size: 11px; color: #64748b; text-align: center; padding: 4px 0;`;
    placeholder.innerText = '✓ Noch keine Fehler';
    this.infractionList.appendChild(placeholder);
    this.scoreValue.innerText = '100';
    this.scoreValue.style.color = '#22c55e';
    const scorePillVal = document.getElementById('hud-score-pill-val');
    if (scorePillVal) {
      scorePillVal.innerText = '100';
      scorePillVal.style.color = '#4ade80';
    }
    this.preDriveCompleted = false;
    this.seatMirrorsAdjusted = false;
    if (this.checklistCard) {
      this.checklistCard.setAttribute('data-collapsed', 'false');
      this.checklistCard.style.borderColor = '#3b82f6';
      this.checklistCard.style.boxShadow = '0 12px 36px rgba(0, 0, 0, 0.8)';
      const itemsList = document.getElementById('hud-checklist-items');
      if (itemsList) itemsList.style.display = 'flex';
    }
  }

  public mount(root: HTMLElement) {
    root.appendChild(this.container);
    this.setCameraMode('cockpit');
  }
}

